<template>
  <div class="mobile-assistant-builder">
    <div class="mt-16 pb-16">
      <!-- Progress Indicator -->
      <v-card class="mb-4" elevation="2">
        <v-card-text class="py-4">
          <div class="text-center mb-2">
            <span class="text-caption text--secondary"
              >پیشرفت تکمیل ساخت دستیار</span
            >
          </div>
          <v-progress-linear
            :value="(completedSections / totalSections) * 100"
            color="primary"
            height="8"
            rounded
          />
          <div class="text-center mt-2">
            <span class="text-body-2 font-weight-bold">
              {{ Math.round((completedSections / totalSections) * 100) }}%
            </span>
          </div>
        </v-card-text>
      </v-card>

      <!-- Sections List -->
      <div v-if="currentStep === 'list'">
        <v-card
          v-for="section in sections"
          :key="section.key"
          class="mb-3 section-card"
          elevation="2"
          :class="getSectionCardClass(section.status)"
          @click="openSection(section.key)"
        >
          <v-card-text class="py-3">
            <div class="d-flex align-center">
              <div
                class="section-icon-wrapper mr-4"
                :class="getStatusIconClass(section.status)"
              >
                <v-icon :color="getStatusColor(section.status)" size="28">
                  {{ getStatusIcon(section.status) }}
                </v-icon>
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-subtitle-1 font-weight-bold">
                      {{ section.title }}
                    </div>
                    <div
                      class="text-caption mt-1"
                      :class="`text--${getStatusTextColor(section.status)}`"
                    >
                      {{ getStatusText(section.status) }}
                    </div>
                  </div>
                  <v-icon color="grey">mdi-chevron-left</v-icon>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Section Content -->
      <div v-else>
        <v-card
          v-for="section in sections"
          v-show="currentStep === section.key"
          :key="section.key"
          class="section-content-card"
          elevation="0"
        >
          <!-- Section Header -->
          <v-card-text class="section-header pa-4">
            <div class="d-flex align-center mb-3">
              <v-btn icon @click="backToList" class="mr-2">
                <v-icon>mdi-arrow-right</v-icon>
              </v-btn>
              <v-icon :color="section.color" class="mr-3" size="28">
                {{ section.icon }}
              </v-icon>
              <div class="text-h6 font-weight-bold">{{ section.title }}</div>
            </div>
          </v-card-text>

          <v-divider />

          <!-- Section Body -->
          <v-card-text class="section-body pa-4">
            <!-- Basic Info Section -->
            <div v-if="section.key === 'basic'">
              <v-text-field
                v-model="formData.assistantName"
                label="نام دستیار *"
                outlined
                dense
                class="mb-3"
                :rules="[rules.required]"
              />
              <v-autocomplete
                v-model="formData.assistantType"
                :items="assistantTypes"
                label="نوع دستیار *"
                outlined
                dense
                class="mb-3"
                clearable
                :rules="[rules.required]"
              />
              <v-autocomplete
                v-model="formData.productServiceType"
                :items="productServiceTypes"
                label="نوع محصول/خدمت *"
                outlined
                dense
                clearable
                :rules="[rules.required]"
              />
            </div>

            <!-- Industry Section -->
            <div v-if="section.key === 'industry'">
              <v-autocomplete
                v-model="selectedMainIndustry"
                :items="mainIndustries"
                item-text="name"
                item-value="id"
                label="انتخاب صنعت اصلی"
                outlined
                dense
                class="mb-3"
                clearable
                @change="onMainIndustryChange"
              />
              <v-autocomplete
                v-if="selectedMainIndustry"
                v-model="formData.selectedIndustries"
                :items="availableSubIndustries"
                label="انتخاب زیر صنایع (حداکثر ۵ مورد)"
                outlined
                dense
                multiple
                chips
                deletable-chips
                :max="5"
                :rules="[rules.maxFive]"
              />
            </div>

            <!-- Product Specs Section -->
            <div v-if="section.key === 'product'">
              <v-autocomplete
                v-model="formData.organizationType"
                :items="organizationTypes"
                label="مشخصات مجموعه / سازمان"
                outlined
                dense
                class="mb-3"
                clearable
              />
              <v-autocomplete
                v-model="formData.productServiceTypeDetail"
                :items="productServiceTypeDetails"
                label="نوع محصول / خدمت"
                outlined
                dense
                class="mb-3"
                clearable
              />
              <v-autocomplete
                v-model="formData.brandSpec"
                :items="brandSpecs"
                label="مشخصات برند"
                outlined
                dense
                class="mb-3"
                clearable
              />
              <v-autocomplete
                v-model="formData.referenceStyle"
                :items="referenceStyles"
                label="نحوه اشاره در پاسخ"
                outlined
                dense
                clearable
              />
            </div>

            <!-- Goals Section -->
            <div v-if="section.key === 'goals'">
              <v-autocomplete
                v-model="formData.salesGoals"
                :items="salesGoals"
                label="فروش و تبدیل"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
              />
              <v-autocomplete
                v-model="formData.supportGoals"
                :items="supportGoals"
                label="پشتیبانی و عملیات"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
              />
              <v-autocomplete
                v-model="formData.engagementGoals"
                :items="engagementGoals"
                label="جذب و ارتباط"
                outlined
                dense
                multiple
                chips
                deletable-chips
              />
            </div>

            <!-- Tone Section -->
            <div v-if="section.key === 'tone'">
              <v-autocomplete
                v-model="formData.tone"
                :items="tones"
                label="لحن کلی"
                outlined
                dense
                class="mb-3"
                clearable
              />
              <v-autocomplete
                v-model="formData.personality"
                :items="personalities"
                label="شخصیت دستیار"
                outlined
                dense
                clearable
              />
            </div>

            <!-- Response Structure Section -->
            <div v-if="section.key === 'structure'">
              <v-autocomplete
                v-model="formData.responseLength"
                :items="responseLengths"
                label="طول پاسخ"
                outlined
                dense
                class="mb-3"
                clearable
              />
              <v-autocomplete
                v-model="formData.preferredStructure"
                :items="preferredStructures"
                label="ساختار ترجیحی"
                outlined
                dense
                clearable
              />
            </div>

            <!-- Information Level Section -->
            <div v-if="section.key === 'information'">
              <v-autocomplete
                v-model="formData.informationSource"
                :items="informationSources"
                label="منبع اطلاعات"
                outlined
                dense
                class="mb-3"
                clearable
              />
              <v-autocomplete
                v-model="formData.noInformationBehavior"
                :items="noInformationBehaviors"
                label="عدم وجود اطلاعات دقیق"
                outlined
                dense
                clearable
              />
            </div>

            <!-- Constraints Section -->
            <div v-if="section.key === 'constraints'">
              <v-autocomplete
                v-model="formData.contentSensitivity"
                :items="contentSensitivities"
                label="حساسیت محتوا"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('contentSensitivity')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-autocomplete
                v-model="formData.claimControl"
                :items="claimControls"
                label="کنترل ادعا"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('claimControl')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-autocomplete
                v-model="formData.privacySecurity"
                :items="privacySecurities"
                label="حریم خصوصی و امنیت"
                outlined
                dense
                multiple
                chips
                deletable-chips
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('privacySecurity')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>

            <!-- Keywords Section -->
            <div v-if="section.key === 'keywords'">
              <v-autocomplete
                v-model="formData.generalKeywords"
                :items="generalKeywords"
                label="عمومی"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('generalKeywords')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-autocomplete
                v-model="formData.technicalKeywords"
                :items="technicalKeywords"
                label="محصول فنی"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('technicalKeywords')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-autocomplete
                v-model="formData.b2bKeywords"
                :items="b2bKeywords"
                label="B2B"
                outlined
                dense
                multiple
                chips
                deletable-chips
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('b2bKeywords')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>

            <!-- Call to Action Section -->
            <div v-if="section.key === 'cta'">
              <v-autocomplete
                v-model="formData.callToActionType"
                :items="callToActionTypes"
                label="نوع فراخوان"
                outlined
                dense
                class="mb-3"
                clearable
                :custom-filter="customFilter"
              />
              <v-text-field
                v-if="formData.callToActionType === 'custom'"
                v-model="formData.customCallToAction"
                label="متن فراخوان دلخواه"
                outlined
                dense
              />
            </div>

            <!-- Decision Logic Section -->
            <div v-if="section.key === 'decision'">
              <v-autocomplete
                v-model="formData.goalPriority"
                :items="goalPriorities"
                label="اولویت در تعارض اهداف"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('goalPriority')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-autocomplete
                v-model="formData.noInfoBehavior"
                :items="noInfoBehaviors"
                label="رفتار در نبود اطلاعات کافی"
                outlined
                dense
                multiple
                chips
                deletable-chips
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('noInfoBehavior')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>

            <!-- Quality Control Section -->
            <div v-if="section.key === 'quality'">
              <v-autocomplete
                v-model="formData.qualityControl"
                :items="qualityControls"
                label="کنترل کیفیت"
                outlined
                dense
                multiple
                chips
                deletable-chips
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('qualityControl')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>

            <!-- Sentiment Section -->
            <div v-if="section.key === 'sentiment'">
              <v-autocomplete
                v-model="formData.emotionalDetection"
                :items="emotionalDetections"
                label="نوع تشخیص"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('emotionalDetection')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-autocomplete
                v-model="formData.emotionalReaction"
                :items="emotionalReactions"
                label="واکنش پیشنهادی"
                outlined
                dense
                multiple
                chips
                deletable-chips
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('emotionalReaction')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>

            <!-- Brand DNA Section -->
            <div v-if="section.key === 'brand'">
              <v-autocomplete
                v-model="formData.brandPolicy"
                :items="brandPolicies"
                label="خط مشی برند"
                outlined
                dense
                multiple
                chips
                deletable-chips
                class="mb-3"
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item @click="addCustomOption('brandPolicy')">
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
              <v-text-field
                v-model="formData.bannedWords"
                label="لیست کلمات ممنوعه (با کاما جدا کنید)"
                outlined
                dense
                hint="کلمات را با کاما جدا کنید"
                persistent-hint
              />
            </div>

            <!-- Repetition Section -->
            <div v-if="section.key === 'repetition'">
              <v-autocomplete
                v-model="formData.repeatQuestionBehavior"
                :items="repeatQuestionBehaviors"
                label="رفتار در تکرار"
                outlined
                dense
                multiple
                chips
                deletable-chips
                :custom-filter="customFilter"
              >
                <template v-slot:append-item>
                  <v-list-item
                    @click="addCustomOption('repeatQuestionBehavior')"
                  >
                    <v-list-item-content>
                      <v-btn text color="primary" small>
                        <v-icon left small>mdi-plus</v-icon>
                        افزودن مورد جدید
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>

            <!-- Additional Info Section -->
            <div v-if="section.key === 'additional'">
              <v-textarea
                v-model="formData.additionalInfo"
                label="توضیحات"
                outlined
                rows="5"
                hint="هر گونه اطلاعات اضافی که می‌خواهید به دستیار اضافه شود"
                persistent-hint
              />
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Summary Tab -->
      <div v-if="currentStep === 'summary'">
        <v-card elevation="2">
          <v-card-title class="text-h6">
            <v-icon left color="primary">mdi-check-circle</v-icon>
            خلاصه انتخاب‌ها
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <div class="mb-4">
              <div class="text-subtitle-2 mb-2 font-weight-bold">
                مشخصات پایه
              </div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.assistantName"
                  close
                  class="ma-1 chip-base"
                  @click:close="clearField('assistantName')"
                >
                  نام: {{ formData.assistantName }}
                </v-chip>
                <v-chip
                  v-if="formData.assistantType"
                  close
                  class="ma-1 chip-base"
                  @click:close="clearField('assistantType')"
                >
                  نوع: {{ formData.assistantType }}
                </v-chip>
                <v-chip
                  v-if="formData.productServiceType"
                  close
                  class="ma-1 chip-base"
                  @click:close="clearField('productServiceType')"
                >
                  محصول/خدمت: {{ formData.productServiceType }}
                </v-chip>
              </div>
            </div>

            <!-- Industry Chips -->
            <div v-if="formData.selectedIndustries.length > 0" class="mb-4">
              <div class="text-subtitle-2 mb-2 font-weight-bold">صنعت</div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.selectedMainIndustry"
                  close
                  class="ma-1 chip-industry"
                  @click:close="
                    formData.selectedMainIndustry = null;
                    formData.selectedIndustries = [];
                  "
                >
                  صنعت اصلی: {{ formData.selectedMainIndustry }}
                </v-chip>
                <v-chip
                  v-for="(industry, index) in formData.selectedIndustries"
                  :key="index"
                  close
                  class="ma-1 chip-industry"
                  @click:close="
                    removeFromArray(formData.selectedIndustries, industry)
                  "
                >
                  {{ industry }}
                </v-chip>
              </div>
            </div>

            <!-- Other sections chips similarly... -->
            <!-- برای کوتاه شدن، فقط چند نمونه نوشتم -->
            <!-- می‌توانید همه چیپ‌ها را مثل کامپوننت اصلی اضافه کنید -->
          </v-card-text>
        </v-card>
      </div>
    </div>
    <v-card-actions class="mt-4 dcolumn">
      <v-spacer />
      <v-btn
        outlined
        class="btn-upload rounded-lg mb2 w-100"
        :loading="loading"
        @click="showSummaryDialog = true"
      >
        خلاصه تنظیمات
        <v-icon left>mdi-clipboard-list</v-icon>
      </v-btn>
      <v-btn
        class="rounded-lg mb2 btn-get-code w-100"
        :loading="loading"
        @click="save"
      >
        {{ assistantData ? "به‌روزرسانی دستیار" : "ساخت دستیار" }}
        <v-icon left>mdi-robot</v-icon>
      </v-btn>
      <v-btn
        class="rounded-lg mb2 btn-edit w-100"
        outlined
        @click="refreshForm"
      >
        بازنشانی دستیار <v-icon left>mdi-refresh</v-icon></v-btn
      >
      <v-btn class="rounded-lg mb2 btn-delete w-100" outlined @click="cancel">
        انصراف <v-icon left>mdi-cancel</v-icon></v-btn
      >
    </v-card-actions>

    <!-- Dialog خلاصه تنظیمات -->
    <custom-popup
      v-model="showSummaryDialog"
      :cons="{ title: 'خلاصه تنظیمات دستیار', icon: 'clipboard-list' }"
      @close="showSummaryDialog = false"
      max-width="900"
      scrollable
      hide-confirm
      hide-overlay
    >
      <v-card>
        <v-card-text class="pa-4">
          <!-- مشخصات پایه -->
          <v-card outlined class="mb-4" style="border-right: 4px solid #3b82f6">
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #3b82f6"
            >
              <v-icon left small>mdi-robot</v-icon>
              مشخصات پایه
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.assistantName"
                  class="ma-1 chip-base"
                  close
                  @click:close="clearField('assistantName')"
                >
                  نام: {{ formData.assistantName }}
                </v-chip>
                <v-chip
                  v-if="formData.assistantType"
                  class="ma-1 chip-base"
                  close
                  @click:close="clearField('assistantType')"
                >
                  نوع: {{ formData.assistantType }}
                </v-chip>
                <v-chip
                  v-if="formData.productServiceType"
                  class="ma-1 chip-base"
                  close
                  @click:close="clearField('productServiceType')"
                >
                  محصول/خدمت: {{ formData.productServiceType }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- صنعت -->
          <v-card
            v-if="
              formData.selectedIndustries?.length > 0 ||
              formData.selectedMainIndustry
            "
            outlined
            class="mb-4"
            style="border-right: 4px solid #10b981"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #10b981"
            >
              <v-icon left small>mdi-factory</v-icon>
              صنعت/حوزه فعالیت
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.selectedMainIndustry"
                  class="ma-1 chip-industry"
                  close
                  @click:close="
                    formData.selectedMainIndustry = null;
                    formData.selectedIndustries = [];
                  "
                >
                  صنعت اصلی: {{ formData.selectedMainIndustry }}
                </v-chip>
                <v-chip
                  v-for="(industry, index) in formData.selectedIndustries"
                  :key="index"
                  class="ma-1 chip-industry"
                  close
                  @click:close="
                    removeFromArray(formData.selectedIndustries, industry)
                  "
                >
                  {{ industry }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- مشخصات محصول -->
          <v-card
            v-if="
              formData.organizationType ||
              formData.brandSpec ||
              formData.referenceStyle
            "
            outlined
            class="mb-4"
            style="border-right: 4px solid #6366f1"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #6366f1"
            >
              <v-icon left small>mdi-package-variant</v-icon>
              مشخصات محصول/خدمت
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.organizationType"
                  class="ma-1 chip-product"
                  close
                  @click:close="clearField('organizationType')"
                >
                  سازمان: {{ formData.organizationType }}
                </v-chip>
                <v-chip
                  v-if="formData.brandSpec"
                  class="ma-1 chip-product"
                  close
                  @click:close="clearField('brandSpec')"
                >
                  برند: {{ formData.brandSpec }}
                </v-chip>
                <v-chip
                  v-if="formData.referenceStyle"
                  class="ma-1 chip-product"
                  close
                  @click:close="clearField('referenceStyle')"
                >
                  اشاره: {{ formData.referenceStyle }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- اهداف -->
          <v-card
            v-if="
              formData.salesGoals?.length > 0 ||
              formData.supportGoals?.length > 0 ||
              formData.engagementGoals?.length > 0
            "
            outlined
            class="mb-4"
            style="border-right: 4px solid #f59e0b"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #f59e0b"
            >
              <v-icon left small>mdi-target</v-icon>
              هدف دستیار
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(goal, index) in formData.salesGoals"
                  :key="'sales-' + index"
                  class="ma-1 chip-goals"
                  close
                  @click:close="removeFromArray(formData.salesGoals, goal)"
                >
                  فروش: {{ goal }}
                </v-chip>
                <v-chip
                  v-for="(goal, index) in formData.supportGoals"
                  :key="'support-' + index"
                  class="ma-1 chip-goals"
                  close
                  @click:close="removeFromArray(formData.supportGoals, goal)"
                >
                  پشتیبانی: {{ goal }}
                </v-chip>
                <v-chip
                  v-for="(goal, index) in formData.engagementGoals"
                  :key="'engagement-' + index"
                  class="ma-1 chip-goals"
                  close
                  @click:close="removeFromArray(formData.engagementGoals, goal)"
                >
                  جذب: {{ goal }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- لحن و شخصیت -->
          <v-card
            v-if="formData.tone || formData.personality"
            outlined
            class="mb-4"
            style="border-right: 4px solid #ec4899"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #ec4899"
            >
              <v-icon left small>mdi-comment-text-outline</v-icon>
              لحن و شخصیت
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.tone"
                  class="ma-1 chip-tone"
                  close
                  @click:close="clearField('tone')"
                >
                  لحن: {{ formData.tone }}
                </v-chip>
                <v-chip
                  v-if="formData.personality"
                  class="ma-1 chip-tone"
                  close
                  @click:close="clearField('personality')"
                >
                  شخصیت: {{ formData.personality }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- ساختار پاسخ -->
          <v-card
            v-if="formData.responseLength || formData.preferredStructure"
            outlined
            class="mb-4"
            style="border-right: 4px solid #84cc16"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #84cc16"
            >
              <v-icon left small>mdi-format-list-bulleted</v-icon>
              ساختار پاسخ
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.responseLength"
                  class="ma-1 chip-structure"
                  close
                  @click:close="clearField('responseLength')"
                >
                  طول: {{ formData.responseLength }}
                </v-chip>
                <v-chip
                  v-if="formData.preferredStructure"
                  class="ma-1 chip-structure"
                  close
                  @click:close="clearField('preferredStructure')"
                >
                  ساختار: {{ formData.preferredStructure }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- سطح اطلاعات -->
          <v-card
            v-if="formData.informationSource || formData.noInformationBehavior"
            outlined
            class="mb-4"
            style="border-right: 4px solid #06b6d4"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #06b6d4"
            >
              <v-icon left small>mdi-information</v-icon>
              سطح اطلاعات
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.informationSource"
                  class="ma-1 chip-info"
                  close
                  @click:close="clearField('informationSource')"
                >
                  منبع: {{ formData.informationSource }}
                </v-chip>
                <v-chip
                  v-if="formData.noInformationBehavior"
                  class="ma-1 chip-info"
                  close
                  @click:close="clearField('noInformationBehavior')"
                >
                  عدم اطلاعات: {{ formData.noInformationBehavior }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- محدودیت‌ها -->
          <v-card
            v-if="
              formData.contentSensitivity?.length > 0 ||
              formData.claimControl?.length > 0 ||
              formData.privacySecurity?.length > 0
            "
            outlined
            class="mb-4"
            style="border-right: 4px solid #ef4444"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #ef4444"
            >
              <v-icon left small>mdi-lock</v-icon>
              محدودیت‌ها
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(item, index) in formData.contentSensitivity"
                  :key="'sensitivity-' + index"
                  class="ma-1 chip-restrictions"
                  close
                  @click:close="
                    removeFromArray(formData.contentSensitivity, item)
                  "
                >
                  {{ item }}
                </v-chip>
                <v-chip
                  v-for="(item, index) in formData.claimControl"
                  :key="'claim-' + index"
                  class="ma-1 chip-restrictions"
                  close
                  @click:close="removeFromArray(formData.claimControl, item)"
                >
                  {{ item }}
                </v-chip>
                <v-chip
                  v-for="(item, index) in formData.privacySecurity"
                  :key="'privacy-' + index"
                  class="ma-1 chip-restrictions"
                  close
                  @click:close="removeFromArray(formData.privacySecurity, item)"
                >
                  {{ item }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- کلیدواژه‌ها -->
          <v-card
            v-if="
              formData.generalKeywords?.length > 0 ||
              formData.technicalKeywords?.length > 0 ||
              formData.b2bKeywords?.length > 0
            "
            outlined
            class="mb-4"
            style="border-right: 4px solid #8b5cf6"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #8b5cf6"
            >
              <v-icon left small>mdi-key</v-icon>
              کلیدواژه‌ها
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(keyword, index) in formData.generalKeywords"
                  :key="'general-' + index"
                  class="ma-1 chip-keywords"
                  close
                  @click:close="
                    removeFromArray(formData.generalKeywords, keyword)
                  "
                >
                  عمومی: {{ keyword }}
                </v-chip>
                <v-chip
                  v-for="(keyword, index) in formData.technicalKeywords"
                  :key="'technical-' + index"
                  class="ma-1 chip-keywords"
                  close
                  @click:close="
                    removeFromArray(formData.technicalKeywords, keyword)
                  "
                >
                  فنی: {{ keyword }}
                </v-chip>
                <v-chip
                  v-for="(keyword, index) in formData.b2bKeywords"
                  :key="'b2b-' + index"
                  class="ma-1 chip-keywords"
                  close
                  @click:close="removeFromArray(formData.b2bKeywords, keyword)"
                >
                  B2B: {{ keyword }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- CTA -->
          <v-card
            v-if="formData.callToActionType || formData.customCallToAction"
            outlined
            class="mb-4"
            style="border-right: 4px solid #a855f7"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #a855f7"
            >
              <v-icon left small>mdi-hand-pointing-right</v-icon>
              نوع فراخوان
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-if="formData.callToActionType"
                  class="ma-1 chip-cta"
                  close
                  @click:close="clearField('callToActionType')"
                >
                  {{
                    formData.callToActionType === "custom"
                      ? formData.customCallToAction
                      : formData.callToActionType
                  }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- منطق تصمیم‌گیری -->
          <v-card
            v-if="
              formData.goalPriority?.length > 0 ||
              formData.noInfoBehavior?.length > 0
            "
            outlined
            class="mb-4"
            style="border-right: 4px solid #f97316"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #f97316"
            >
              <v-icon left small>mdi-lightbulb-on</v-icon>
              منطق تصمیم‌گیری
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(item, index) in formData.goalPriority"
                  :key="'goal-' + index"
                  class="ma-1 chip-logic"
                  close
                  @click:close="removeFromArray(formData.goalPriority, item)"
                >
                  {{ item }}
                </v-chip>
                <v-chip
                  v-for="(item, index) in formData.noInfoBehavior"
                  :key="'noinfo-' + index"
                  class="ma-1 chip-logic"
                  close
                  @click:close="removeFromArray(formData.noInfoBehavior, item)"
                >
                  {{ item }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- کنترل کیفیت -->
          <v-card
            v-if="formData.qualityControl?.length > 0"
            outlined
            class="mb-4"
            style="border-right: 4px solid #3b82f6"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #3b82f6"
            >
              <v-icon left small>mdi-check-circle</v-icon>
              کنترل کیفیت
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(item, index) in formData.qualityControl"
                  :key="'quality-' + index"
                  class="ma-1 chip-quality"
                  close
                  @click:close="removeFromArray(formData.qualityControl, item)"
                >
                  {{ item }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- تحلیل احساسی -->
          <v-card
            v-if="
              formData.emotionalDetection?.length > 0 ||
              formData.emotionalReaction?.length > 0
            "
            outlined
            class="mb-4"
            style="border-right: 4px solid #ec4899"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #ec4899"
            >
              <v-icon left small>mdi-emoticon-happy</v-icon>
              تحلیل احساسی
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(item, index) in formData.emotionalDetection"
                  :key="'detection-' + index"
                  class="ma-1 chip-emotion"
                  close
                  @click:close="
                    removeFromArray(formData.emotionalDetection, item)
                  "
                >
                  تشخیص: {{ item }}
                </v-chip>
                <v-chip
                  v-for="(item, index) in formData.emotionalReaction"
                  :key="'reaction-' + index"
                  class="ma-1 chip-emotion"
                  close
                  @click:close="
                    removeFromArray(formData.emotionalReaction, item)
                  "
                >
                  واکنش: {{ item }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- خط مشی برند -->
          <v-card
            v-if="formData.brandPolicy?.length > 0 || formData.bannedWords"
            outlined
            class="mb-4"
            style="border-right: 4px solid #dc2626"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #dc2626"
            >
              <v-icon left small>mdi-brain</v-icon>
              خط مشی برند
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(item, index) in formData.brandPolicy"
                  :key="'policy-' + index"
                  class="ma-1 chip-brand"
                  close
                  @click:close="removeFromArray(formData.brandPolicy, item)"
                >
                  {{ item }}
                </v-chip>
                <v-chip
                  v-for="(word, index) in formData.bannedWords
                    ?.split(/[,،]/)
                    .map((w) => w.trim())
                    .filter((w) => w) || []"
                  :key="'banned-' + index"
                  class="ma-1 chip-brand"
                  close
                  @click:close="removeBannedWord(word)"
                >
                  ممنوعه: {{ word }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- رفتار در تکرار -->
          <v-card
            v-if="formData.repeatQuestionBehavior?.length > 0"
            outlined
            class="mb-4"
            style="border-right: 4px solid #84cc16"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #84cc16"
            >
              <v-icon left small>mdi-refresh</v-icon>
              رفتار در تکرار
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(item, index) in formData.repeatQuestionBehavior"
                  :key="'repeat-' + index"
                  class="ma-1 chip-repeat"
                  close
                  @click:close="
                    removeFromArray(formData.repeatQuestionBehavior, item)
                  "
                >
                  {{ item }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- اطلاعات تکمیلی -->
          <v-card
            v-if="formData.additionalInfo"
            outlined
            class="mb-4"
            style="border-right: 4px solid #6b7280"
          >
            <v-card-title
              class="text-subtitle-1 font-weight-bold"
              style="color: #6b7280"
            >
              <v-icon left small>mdi-note-text</v-icon>
              اطلاعات تکمیلی
            </v-card-title>
            <v-card-text>
              <div class="text-body-2">{{ formData.additionalInfo }}</div>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-divider />
      </v-card>
    </custom-popup>
    <!-- دیالوگ افزودن مورد جدید -->
    <custom-popup
      v-model="showAddCustomDialog"
      :cons="{ title: 'افزودن مورد جدید' }"
      @close="showAddCustomDialog = false"
      max-width="500"
    >
      <v-card>
        <v-card-text>
          <v-text-field
            v-model="customOptionText"
            label="متن مورد جدید"
            outlined
            dense
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="showAddCustomDialog = false"
            class="btn-delete rounded-lg"
            >لغو</v-btn
          >
          <v-btn @click="addCustomOptionConfirm" class="btn-get-code rounded-lg"
            >افزودن</v-btn
          >
        </v-card-actions>
      </v-card>
    </custom-popup>
  </div>
</template>

<script>
export default {
  name: "PersonalAssistantBuilderMobile",
  props: {
    assistantData: {
      type: Object,
      default: null,
    },
    // formData: {
    //   type: Object,
    //   required: true,
    // },
  },
  data() {
    return {
      currentStep: "list", // 'list' or section key or 'summary'
      bottomNav: 0,
      loading: false,
      showAddCustomDialog: false,
      customOptionText: "",
      currentCustomField: null,
      selectedMainIndustry: null,
      skippedSections: [],
      showSummaryDialog: false,
      mappings: {
        // === مشخصات پایه ===
        assistantType: {
          "دستیار فروش": "sales_assistant",
          "دستیار پشتیبانی": "support_assistant",
          "دستیار راهنما": "guide_assistant",
          "دستیار مشاور": "consultant_assistant",
          "دستیار اختصاصی برند": "brand_dedicated_assistant",
          "دستیار پاسخ‌گوی واتساپ/دایرکت": "social_media_assistant",
          "دستیار پاسخ‌گوی سایت": "website_chat_assistant",
          "دستیار پاسخ‌گوی تلفنی": "phone_assistant",
          "دستیار آموزش محصول": "product_training_assistant",
          "دستیار پیگیری سفارش": "order_tracking_assistant",
          "دستیار قبل از خرید": "pre_sales_assistant",
          "دستیار بعد از خرید": "post_sales_assistant",
          "دستیار سازمانی (B2B)": "b2b_assistant",
          "دستیار نمایندگی‌ها": "dealer_assistant",
          "دستیار خدمات پس از فروش": "after_sales_assistant",
          "دستیار ثبت شکایت و بازخورد": "complaint_feedback_assistant",
          "دستیار محتوای محصول (FAQ ساز)": "faq_assistant",
          "دستیار مقایسه و انتخاب محصول": "product_comparison_assistant",
        },

        productServiceType: {
          "محصول فیزیکی (کالای مصرفی)": "consumer_physical_product",
          "محصول فیزیکی (کالای بادوام)": "durable_physical_product",
          "خدمت حضوری": "in_person_service",
          "خدمت آنلاین": "online_service",
          "نرم‌افزار / اپلیکیشن (B2C)": "b2c_software_app",
          "نرم‌افزار / SaaS (B2B)": "b2b_saas",
          "پلتفرم / مارکت‌پلیس": "platform_marketplace",
          "اشتراک / عضویت": "subscription_membership",
          "دوره آموزشی / محتوا": "educational_content",
          "سرویس مالی (غیرمشاوره‌ای)": "financial_service_non_advisory",
          "سرویس سلامت عمومی (غیرپزشکی)": "general_health_service",
          "رسانه / سرگرمی / محتوا": "media_entertainment",
          "رویداد / همایش / ورکشاپ": "event_workshop",
          "سازمان / برند چندمحصولی": "multi_product_brand",
        },

        // === صنعت ===
        industryMain: {
          "صنایع مصرفی و فیزیکی": "consumer_physical",
          "دیجیتال و فناوری": "digital_tech",
          "خدمات و B2B": "services_b2b",
          "آموزش و دوره آموزشی": "education_training",
          "سلامت و سبک زندگی": "health_lifestyle",
          "رسانه، فرهنگ، آموزش و محتوا": "media_culture_content",
        },

        industrySub: {},

        // === مشخصات محصول/خدمت ===
        organizationType: {
          "تولید کننده": "manufacturer",
          "توزیع کننده": "distributor",
          فروشگاه: "retailer",
          استارتاپ: "startup",
          "شرکت خدماتی": "service_company",
          "سازمان B2B": "b2b_organization",
          نمایندگی: "dealership",
        },

        brandSpec: {
          اقتصادی: "economy",
          "میان رده": "mid_range",
          پریمیوم: "premium",
          لوکس: "luxury",
          "سازمانی / شرکتی": "corporate",
          "نوآور / استارتاپی": "innovative_startup",
        },

        referenceStyle: {
          "فقط نام محصول": "product_name_only",
          "محصول + برند": "product_and_brand",
          "برند + سازمان": "brand_and_company",
          "محصول + برند + سازمان": "product_brand_and_company",
        },

        // === لحن و شخصیت ===
        tone: {
          "کاملاً رسمی": "very_formal",
          "رسمی و حرفه‌ای": "formal_professional",
          نیمه‌رسمی: "semi_formal",
          دوستانه: "friendly",
          صمیمی: "warm_intimate",
          "لوکس و پریمیوم": "luxury_premium",
          "جوان‌پسند و پرانرژی": "youthful_energetic",
          "مینیمال و خیلی کوتاه": "minimal_very_short",
          "تبلیغاتی (اما بدون اغراق)": "promotional_no_exaggeration",
          مشاوره‌ای: "consultative",
          "فنی/مهندسی": "technical_engineering",
          "آموزشی/مدرس‌گونه": "educational",
          "همدلانه (پشتیبانی‌محور)": "empathetic_support",
        },

        personality: {
          "دقیق و فنی": "precise_technical",
          "ساده و قابل‌فهم": "simple_understandable",
          متقاعدکننده: "persuasive",
          نتیجه‌محور: "results_oriented",
          "سوال‌محور (با پرسش‌های کوتاه)": "question_based",
          "حرفه‌ای و خونسرد": "professional_calm",
          "گرم و صمیمی": "warm_friendly",
          "سخت‌گیر در اطلاعات (بدون حدس)": "strict_no_guessing",
          "داستان‌گو (برای بازاریابی)": "storyteller_marketing",
          "مقایسه‌گر منطقی": "logical_comparer",
        },

        // === ساختار پاسخ ===
        responseLength: {
          "خیلی کوتاه (۱–۲ جمله)": "very_short",
          "کوتاه (۲–۴ جمله)": "short",
          "متوسط (۴–۶ جمله)": "medium",
          "کامل (۷–۱۲ جمله)": "detailed",
          "توضیحی (پاراگرافی)": "explanatory",
          "راهنمای مرحله‌ای": "step_by_step",
          "FAQ سبک (سؤال/جواب کوتاه)": "faq_style",
          چک‌لیست‌محور: "checklist",
          "جدول‌محور (اگر لازم شد)": "table_if_needed",
        },

        preferredStructure: {
          "متن ساده": "plain_text",
          بولت‌پوینت: "bullet_points",
          "مراحل شماره‌دار": "numbered_steps",
          ترکیبی: "combined",
          "سؤال‌های کوتاه برای تکمیل اطلاعات": "clarifying_questions",
          "خلاصه + جزئیات (ابتدا TL;DR)": "summary_first",
        },

        // === سطح اطلاعات ===
        informationSource: {
          "فقط اطلاعاتی که کاربر می‌دهد": "user_provided_only",
          "اطلاعات کاربر + توصیه‌های عمومی ایمن": "user_plus_safe_general",
          "اطلاعات عمومی صنعت (غیرتخصصی)": "general_industry_info",
          "کاتالوگ/FAQ داخلی (اگر داده دادند)": "internal_catalog_faq",
          "بدون حدس و فرض (سخت‌گیر)": "strict_no_assumptions",
          "اطلاعات کاربر + مقایسه کلی بازار (بدون نام برند)":
            "user_plus_general_market_comparison",
        },

        noInformationBehavior: {
          "اعلام عدم‌اطلاعی": "declare_unknown",
          "درخواست اطلاعات بیشتر (عکس/لینک/برچسب)": "ask_for_more_info",
          "ارائه راهنمای کلی": "provide_general_guidance",
          "پیشنهاد مسیر جایگزین (تماس/تیکت)": "suggest_alternative_contact",
          "ترکیب موارد بالا": "combined_approaches",
          "ارائه چند سناریوی محتمل (با برچسب فرضیگ)":
            "possible_scenarios_labeled",
        },

        // === فراخوان به عمل ===
        callToAction: {
          "دعوت به خرید": "invite_to_purchase",
          "درخواست اطلاعات بیشتر": "request_more_info",
          "هدایت به لینک": "direct_to_link",
          "تماس با پشتیبانی": "contact_support",
          ثبت‌نام: "sign_up",
          "رزرو وقت/نوبت": "book_appointment",
          "دریافت کاتالوگ/دمو": "request_catalog_demo",
          "مشاهده قیمت و پلن‌ها": "view_pricing_plans",
          "مقایسه پلن‌ها": "compare_plans",
          "ثبت درخواست همکاری/نمایندگی": "apply_for_partnership",
          "دریافت مشاوره (غیرتخصصی)": "get_general_consultation",
          "بدون دعوت به فراخوانی": "no_call_to_action",
        },

        // === اهداف ===
        goals: {
          // فروش
          "معرفی محصول/خدمت": "introduce_product_service",
          "افزایش فروش": "increase_sales",
          "پیشنهاد محصول مناسب": "recommend_suitable_product",
          "پاسخ به اعتراضات خرید": "handle_objections",
          "آپ‌سل/کراس‌سل (پیشنهاد تکمیلی)": "upsell_crosssell",
          "هدایت به پرداخت/ثبت سفارش": "guide_to_payment",
          // پشتیبانی
          "پاسخ‌گویی به سوالات مشتری (FAQ)": "answer_faqs",
          "راهنمای استفاده/نصب/راه‌اندازی": "usage_setup_guide",
          "پیگیری سفارش/ارسال": "order_tracking",
          "مرجوعی/گارانتی/تعویض": "returns_warranty",
          "ثبت تیکت/شکایت/بازخورد": "log_ticket_complaint",
          "راهنمایی حل مشکل (Troubleshooting)": "troubleshooting",
          // جذب
          "جمع‌آوری لید (نام/شماره/نیاز)": "lead_collection",
          "رزرو وقت/نوبت": "appointment_booking",
          "ثبت‌نام/عضویت": "registration",
          "معرفی نمایندگی/شعب": "introduce_branches",
          "پاسخ‌گویی چندزبانه": "multilingual_support",
          "آموزش و آگاهی‌سازی (قبل از خرید)": "pre_purchase_education",
        },

        // === محدودیت‌ها ===
        constraints: {
          "عدم ارائه توصیه پزشکی": "no_medical_advice",
          "عدم ارائه مشاوره حقوقی": "no_legal_advice",
          "عدم ارائه مشاوره مالی/سرمایه‌گذاری": "no_financial_advice",
          "عدم ارائه اطلاعات فنی خطرناک": "no_dangerous_technical_info",
          "عدم ارائه دستور ساخت مواد/ابزار خطرناک": "no_hazardous_instructions",
          "عدم ادعاهای قطعی/درمانی": "no_definitive_claims",
          "عدم اغراق تبلیغاتی": "no_exaggerated_claims",
          "فقط با داده معتبر (برچسب/کاتالوگ)": "only_verified_data",
          "عدم مقایسه بدون داده معتبر": "no_unverified_comparison",
          "عدم قیمت‌سازی (اگر قیمت نداریم)": "no_price_invention",
          "عدم درخواست اطلاعات حساس (کارت/رمز)": "no_sensitive_data_request",
          "حداقل‌سازی جمع‌آوری داده": "minimize_data_collection",
          "ارجاع به پشتیبانی برای موارد حساس": "refer_sensitive_to_support",
        },

        // === کلیدواژه‌ها ===
        keywords: {
          // عمومی
          "قیمت / هزینه": "price_cost",
          "تخفیف / جشنواره": "discount_promotion",
          "نحوه خرید": "how_to_buy",
          "ارسال / زمان تحویل": "shipping_delivery_time",
          "شرایط مرجوعی": "return_policy",
          گارانتی: "warranty",
          پشتیبانی: "support",
          مقایسه: "comparison",
          موجودی: "availability",
          "روش پرداخت": "payment_method",
          // فنی
          ویژگی‌ها: "features",
          "مزایا و ارزش‌ها": "benefits_value",
          "نحوه استفاده": "how_to_use",
          ایمنی: "safety",
          نگهداری: "maintenance",
          سازگاری: "compatibility",
          "نصب / راه‌اندازی": "installation_setup",
          عیب‌یابی: "troubleshooting",
          "مصرف انرژی/منابع": "energy_resource_consumption",
          "کیفیت/استانداردها": "quality_standards",
          // B2B
          "خرید عمده": "bulk_purchase",
          "قرارداد سازمانی": "corporate_contract",
          "دموی محصول": "product_demo",
          "قیمت‌گذاری پلن‌ها": "pricing_plans",
          "SLA و پشتیبانی سازمانی": "sla_corporate_support",
          "آموزش تیم": "team_training",
        },

        // === منطق تصمیم‌گیری ===
        decisionLogic: {
          "فروش بر پشتیبانی مقدم است": "sales_over_support",
          "پشتیبانی بر فروش مقدم است": "support_over_sales",
          "راهنمایی بی‌طرفانه (بدون فشار)": "neutral_guidance",
          "بسته به نوع سؤال تصمیم‌گیری شود": "context_dependent",
          "توقف پاسخ و اعلام عدم‌اطلاعی": "declare_unknown",
          "درخواست اطلاعات تکمیلی": "ask_for_more_info",
          "ارائه راهنمای کلی ایمن": "provide_safe_general_guidance",
          "پیشنهاد مسیر جایگزین": "suggest_alternative",
        },

        // === کنترل کیفیت ===
        qualityControl: {
          "بررسی تطابق پاسخ با محدودیت‌ها": "check_constraint_compliance",
          "جلوگیری از اغراق تبلیغاتی": "prevent_exaggeration",
          "حذف حدس و فرض‌های نامطمئن": "remove_uncertain_assumptions",
          "ساده‌سازی زبان در پاسخ‌های پیچیده": "simplify_complex_language",
          "کوتاه‌سازی پاسخ‌های بیش‌ازحد طولانی": "shorten_excessively_long",
        },

        // === تحلیل احساسی ===
        sentimentDetection: {
          "تشخیص نارضایتی یا عصبانیت": "detect_dissatisfaction_anger",
          "تشخیص تردید در خرید": "detect_purchase_hesitation",
          "تشخیص عجله یا کم‌حوصلگی": "detect_rush_impatience",
          "تشخیص نیاز به اطمینان‌بخشی": "detect_reassurance_needed",
        },

        sentimentReaction: {
          "کاهش لحن فروش": "reduce_sales_tone",
          "همدلی و عذرخواهی حرفه‌ای": "empathy_professional_apology",
          "ارائه پاسخ کوتاه و سریع": "provide_quick_short_response",
          "ارجاع مستقیم به پشتیبانی انسانی": "direct_referral_to_human_support",
        },

        // === خط مشی برند ===
        brandPolicy: {
          "عدم استفاده از ایموجی": "no_emoji",
          "عدم شوخی یا لحن خودمانی": "no_casual_humor",
          "عدم اشاره به رقبا": "no_competitor_mention",
          "عدم مقایسه مستقیم": "no_direct_comparison",
          "استفاده از واژگان رسمی برند": "use_formal_brand_terminology",
        },

        // === رفتار در تکرار سوال ===
        repetitionBehavior: {
          "پاسخ خلاصه‌تر از قبل": "shorter_summary",
          "ارجاع به پاسخ قبلی": "refer_to_previous_answer",
          "ارائه نسخه مرحله‌ای": "provide_staged_version",
          "پرسش برای شفاف‌سازی نیاز کاربر": "ask_for_clarification",
        },
      },
      valid: false,
      loading: false,
      panels: 0,
      showPromptDialog: false,
      showAddCustomDialog: false,
      customOptionText: "",
      currentCustomField: null,
      selectedSubIndustries: [],
      generatedPrompt: "",

      formData: {
        selectedMainIndustry: null,
        selectedIndustries: [],
        assistantName: "",
        assistantType: null,
        productServiceNature: null,
        productServiceType: null,
        organizationType: null,
        productServiceTypeDetail: null,
        brandSpec: null,
        referenceStyle: null,
        salesGoals: [],
        supportGoals: [],
        engagementGoals: [],
        tone: null,
        personality: null,
        responseLength: null,
        preferredStructure: null,
        informationSource: null,
        noInformationBehavior: null,
        contentSensitivity: [],
        claimControl: [],
        privacySecurity: [],
        generalKeywords: [],
        technicalKeywords: [],
        b2bKeywords: [],
        callToActionType: null,
        customCallToAction: "",
        goalPriority: [],
        noInfoBehavior: [],
        qualityControl: [],
        emotionalDetection: [],
        emotionalReaction: [],
        brandPolicy: [],
        bannedWords: "",
        repeatQuestionBehavior: [],
        additionalInfo: "",
      },

      rules: {
        required: (v) => !!v || "این فیلد الزامی است",
        maxFive: (v) => !v || v.length <= 5 || "حداکثر ۵ مورد قابل انتخاب است",
      },

      // داده‌های استاتیک
      assistantTypes: [
        "دستیار فروش",
        "دستیار پشتیبانی",
        "دستیار راهنما",
        "دستیار مشاور",
        "دستیار اختصاصی برند",
        "دستیار پاسخ‌گوی واتساپ/دایرکت",
        "دستیار پاسخ‌گوی سایت",
        "دستیار پاسخ‌گوی تلفنی",
        "دستیار آموزش محصول",
        "دستیار پیگیری سفارش",
        "دستیار قبل از خرید",
        "دستیار بعد از خرید",
        "دستیار سازمانی (B2B)",
        "دستیار نمایندگی‌ها",
        "دستیار خدمات پس از فروش",
        "دستیار ثبت شکایت و بازخورد",
        "دستیار محتوای محصول (FAQ ساز)",
        "دستیار مقایسه و انتخاب محصول",
      ],

      productServiceTypes: [
        "محصول فیزیکی (کالای مصرفی)",
        "محصول فیزیکی (کالای بادوام)",
        "خدمت حضوری",
        "خدمت آنلاین",
        "نرم‌افزار / اپلیکیشن (B2C)",
        "نرم‌افزار / SaaS (B2B)",
        "پلتفرم / مارکت‌پلیس",
        "اشتراک / عضویت",
        "دوره آموزشی / محتوا",
        "سرویس مالی (غیرمشاوره‌ای)",
        "سرویس سلامت عمومی (غیرپزشکی)",
        "رسانه / سرگرمی / محتوا",
        "رویداد / همایش / ورکشاپ",
        "سازمان / برند چندمحصولی",
      ],

      mainIndustries: [
        {
          id: 1,
          name: "صنایع مصرفی و فیزیکی",
          subIndustries: [
            "مواد غذایی",
            "نوشیدنی",
            "شوینده و بهداشتی",
            "آرایشی و مراقبت پوست/مو",
            "عطر و ادکلن",
            "داروخانه‌ای OTC (بدون مشاوره پزشکی)",
            "پوشاک و مد",
            "کفش و کیف",
            "جواهرات و اکسسوری",
            "لوازم خانگی برقی",
            "لوازم آشپزخانه",
            "مبلمان و دکوراسیون",
            "لوازم خواب و کالای خانه",
            "کالای کودک و نوزاد",
            "لوازم تحریر و کتاب",
            "حیوانات خانگی (غذا/لوازم)",
            "ابزار و یراق / DIY",
            "کشاورزی و باغبانی (مصرفی)",
          ],
        },
        {
          id: 2,
          name: "دیجیتال و فناوری",
          subIndustries: [
            "نرم‌افزار و SaaS",
            "اپلیکیشن موبایل",
            "سرویس ابری / هاستینگ",
            "امنیت سایبری (غیرتخصصی)",
            "هوش مصنوعی (محصول/سرویس)",
            "سخت‌افزار و الکترونیک",
            "موبایل و گجت",
            "گیم و سرگرمی دیجیتال",
            "فین‌تک (پرداخت/کیف‌پول)",
            "رمزارز (اطلاع‌رسانی غیرمالی)",
            "مارکت‌پلیس و پلتفرم",
            "مخابرات و اینترنت",
            "ابزارهای تیمی/CRM/ERP",
          ],
        },
        {
          id: 3,
          name: "خدمات و B2B",
          subIndustries: [
            "آموزش و دوره آموزشی",
            "مشاوره کسب‌وکار (غیرحقوقی/غیرمالی تخصصی)",
            "منابع انسانی و استخدام",
            "خدمات سازمانی (B2B)",
            "املاک و مستغلات",
            "ساخت‌وساز و پیمانکاری",
            "گردشگری و هتل‌داری",
            "حمل‌ونقل و لجستیک",
            "خدمات خودرو (تعمیر/لوازم)",
            "بیمه (اطلاع‌رسانی عمومی)",
            "بانکداری (اطلاع‌رسانی عمومی)",
            "خدمات فنی و تعمیرات",
            "رویدادها و نمایشگاه‌ها",
          ],
        },
        {
          id: 4,
          name: "سلامت و سبک زندگی",
          subIndustries: [
            "فیتنس و ورزش",
            "تغذیه عمومی",
            "سلامت عمومی (غیرپزشکی)",
            "روانشناسی عمومی (غیر درمانی)",
            "زیبایی و خدمات سالن",
            "کلینیک زیبایی (اطلاع‌رسانی غیرپزشکی)",
            "مکمل‌ها (اطلاع‌رسانی عمومی)",
            "توسعه فردی و کوچینگ عمومی",
          ],
        },
        {
          id: 5,
          name: "رسانه، فرهنگ، آموزش و محتوا",
          subIndustries: [
            "رسانه و خبر (غیرسیاسی تخصصی)",
            "تولید محتوا/کپی‌رایتینگ",
            "نشر و کتاب",
            "موسیقی و ویدیو",
            "آموزش مدارس/دانشگاه",
            "آموزش مهارتی/فنی",
            "زبان‌آموزی",
          ],
        },
      ],

      organizationTypes: [
        "تولید کننده",
        "توزیع کننده",
        "فروشگاه",
        "استارتاپ",
        "شرکت خدماتی",
        "سازمان B2B",
        "نمایندگی",
      ],

      productServiceTypeDetails: [
        "محصول فیزیکی",
        "خدمت حضوری",
        "خدمت آنلاین",
        "نرم افزار / اپلیکیشن",
        "اشتراکی / دوره ای",
        "چند محصولی",
      ],

      brandSpecs: [
        "اقتصادی",
        "میان رده",
        "پریمیوم",
        "لوکس",
        "سازمانی / شرکتی",
        "نوآور / استارتاپی",
      ],

      referenceStyles: [
        "فقط نام محصول",
        "محصول + برند",
        "برند + سازمان",
        "محصول + برند + سازمان",
      ],

      salesGoals: [
        "معرفی محصول/خدمت",
        "افزایش فروش",
        "پیشنهاد محصول مناسب",
        "پاسخ به اعتراضات خرید",
        "آپ‌سل/کراس‌سل (پیشنهاد تکمیلی)",
        "هدایت به پرداخت/ثبت سفارش",
      ],

      supportGoals: [
        "پاسخ‌گویی به سوالات مشتری (FAQ)",
        "راهنمای استفاده/نصب/راه‌اندازی",
        "پیگیری سفارش/ارسال",
        "مرجوعی/گارانتی/تعویض",
        "ثبت تیکت/شکایت/بازخورد",
        "راهنمایی حل مشکل (Troubleshooting)",
      ],

      engagementGoals: [
        "جمع‌آوری لید (نام/شماره/نیاز)",
        "رزرو وقت/نوبت",
        "ثبت‌نام/عضویت",
        "معرفی نمایندگی/شعب",
        "پاسخ‌گویی چندزبانه",
        "آموزش و آگاهی‌سازی (قبل از خرید)",
      ],

      tones: [
        "کاملاً رسمی",
        "رسمی و حرفه‌ای",
        "نیمه‌رسمی",
        "دوستانه",
        "صمیمی",
        "لوکس و پریمیوم",
        "جوان‌پسند و پرانرژی",
        "مینیمال و خیلی کوتاه",
        "تبلیغاتی (اما بدون اغراق)",
        "مشاوره‌ای",
        "فنی/مهندسی",
        "آموزشی/مدرس‌گونه",
        "همدلانه (پشتیبانی‌محور)",
      ],

      personalities: [
        "دقیق و فنی",
        "ساده و قابل‌فهم",
        "متقاعدکننده",
        "نتیجه‌محور",
        "سوال‌محور (با پرسش‌های کوتاه)",
        "حرفه‌ای و خونسرد",
        "گرم و صمیمی",
        "سخت‌گیر در اطلاعات (بدون حدس)",
        "داستان‌گو (برای بازاریابی)",
        "مقایسه‌گر منطقی",
      ],

      responseLengths: [
        "خیلی کوتاه (۱–۲ جمله)",
        "کوتاه (۲–۴ جمله)",
        "متوسط (۴–۶ جمله)",
        "کامل (۷–۱۲ جمله)",
        "توضیحی (پاراگرافی)",
        "راهنمای مرحله‌ای",
        "FAQ سبک (سؤال/جواب کوتاه)",
        "چک‌لیست‌محور",
        "جدول‌محور (اگر لازم شد)",
      ],

      preferredStructures: [
        "متن ساده",
        "بولت‌پوینت",
        "مراحل شماره‌دار",
        "ترکیبی",
        "سؤال‌های کوتاه برای تکمیل اطلاعات",
        "خلاصه + جزئیات (ابتدا TL;DR)",
      ],

      informationSources: [
        "فقط اطلاعاتی که کاربر می‌دهد",
        "اطلاعات کاربر + توصیه‌های عمومی ایمن",
        "اطلاعات عمومی صنعت (غیرتخصصی)",
        "کاتالوگ/FAQ داخلی (اگر داده دادند)",
        "بدون حدس و فرض (سخت‌گیر)",
        "اطلاعات کاربر + مقایسه کلی بازار (بدون نام برند)",
      ],

      noInformationBehaviors: [
        "اعلام عدم‌اطلاعی",
        "درخواست اطلاعات بیشتر (عکس/لینک/برچسب)",
        "ارائه راهنمای کلی",
        "پیشنهاد مسیر جایگزین (تماس/تیکت)",
        "ترکیب موارد بالا",
        "ارائه چند سناریوی محتمل (با برچسب فرضیگ)",
      ],

      contentSensitivities: [
        "عدم ارائه توصیه پزشکی",
        "عدم ارائه مشاوره حقوقی",
        "عدم ارائه مشاوره مالی/سرمایه‌گذاری",
        "عدم ارائه اطلاعات فنی خطرناک",
        "عدم ارائه دستور ساخت مواد/ابزار خطرناک",
      ],

      claimControls: [
        "عدم ادعاهای قطعی/درمانی",
        "عدم اغراق تبلیغاتی",
        "فقط با داده معتبر (برچسب/کاتالوگ)",
        "عدم مقایسه بدون داده معتبر",
        "عدم قیمت‌سازی (اگر قیمت نداریم)",
      ],

      privacySecurities: [
        "عدم درخواست اطلاعات حساس (کارت/رمز)",
        "حداقل‌سازی جمع‌آوری داده",
        "ارجاع به پشتیبانی برای موارد حساس",
      ],

      generalKeywords: [
        "قیمت / هزینه",
        "تخفیف / جشنواره",
        "نحوه خرید",
        "ارسال / زمان تحویل",
        "شرایط مرجوعی",
        "گارانتی",
        "پشتیبانی",
        "مقایسه",
        "موجودی",
        "روش پرداخت",
      ],

      technicalKeywords: [
        "ویژگی‌ها",
        "مزایا و ارزش‌ها",
        "نحوه استفاده",
        "ایمنی",
        "نگهداری",
        "سازگاری",
        "نصب / راه‌اندازی",
        "عیب‌یابی",
        "مصرف انرژی/منابع",
        "کیفیت/استانداردها",
      ],

      b2bKeywords: [
        "خرید عمده",
        "قرارداد سازمانی",
        "دموی محصول",
        "قیمت‌گذاری پلن‌ها",
        "SLA و پشتیبانی سازمانی",
        "آموزش تیم",
      ],

      callToActionTypes: [
        "دعوت به خرید",
        "درخواست اطلاعات بیشتر",
        "هدایت به لینک",
        "تماس با پشتیبانی",
        "ثبت‌نام",
        "رزرو وقت/نوبت",
        "دریافت کاتالوگ/دمو",
        "مشاهده قیمت و پلن‌ها",
        "مقایسه پلن‌ها",
        "ثبت درخواست همکاری/نمایندگی",
        "دریافت مشاوره (غیرتخصصی)",
        "بدون دعوت به فراخوانی",
        "custom",
      ],

      goalPriorities: [
        "فروش بر پشتیبانی مقدم است",
        "پشتیبانی بر فروش مقدم است",
        "راهنمایی بی‌طرفانه (بدون فشار)",
        "بسته به نوع سؤال تصمیم‌گیری شود",
      ],

      noInfoBehaviors: [
        "توقف پاسخ و اعلام عدم‌اطلاعی",
        "درخواست اطلاعات تکمیلی",
        "ارائه راهنمای کلی ایمن",
        "پیشنهاد مسیر جایگزین",
      ],

      qualityControls: [
        "بررسی تطابق پاسخ با محدودیت‌ها",
        "جلوگیری از اغراق تبلیغاتی",
        "حذف حدس و فرض‌های نامطمئن",
        "ساده‌سازی زبان در پاسخ‌های پیچیده",
        "کوتاه‌سازی پاسخ‌های بیش‌ازحد طولانی",
      ],

      emotionalDetections: [
        "تشخیص نارضایتی یا عصبانیت",
        "تشخیص تردید در خرید",
        "تشخیص عجله یا کم‌حوصلگی",
        "تشخیص نیاز به اطمینان‌بخشی",
      ],

      emotionalReactions: [
        "کاهش لحن فروش",
        "همدلی و عذرخواهی حرفه‌ای",
        "ارائه پاسخ کوتاه و سریع",
        "ارجاع مستقیم به پشتیبانی انسانی",
      ],

      brandPolicies: [
        "عدم استفاده از ایموجی",
        "عدم شوخی یا لحن خودمانی",
        "عدم اشاره به رقبا",
        "عدم مقایسه مستقیم",
        "استفاده از واژگان رسمی برند",
      ],

      repeatQuestionBehaviors: [
        "پاسخ خلاصه‌تر از قبل",
        "ارجاع به پاسخ قبلی",
        "ارائه نسخه مرحله‌ای",
        "پرسش برای شفاف‌سازی نیاز کاربر",
      ],
      sections: [
        {
          key: "basic",
          title: "مشخصات پایه",
          icon: "mdi-robot",
          color: "#3b82f6",
        },
        {
          key: "industry",
          title: "صنعت/حوزه فعالیت",
          icon: "mdi-factory",
          color: "#10b981",
        },
        {
          key: "product",
          title: "مشخصات محصول/خدمت",
          icon: "mdi-package-variant",
          color: "#6366f1",
        },
        {
          key: "goals",
          title: "هدف دستیار",
          icon: "mdi-target",
          color: "#f59e0b",
        },
        {
          key: "tone",
          title: "لحن و شخصیت",
          icon: "mdi-comment-text-outline",
          color: "#ec4899",
        },
        {
          key: "structure",
          title: "ساختار پاسخ",
          icon: "mdi-format-list-bulleted",
          color: "#84cc16",
        },
        {
          key: "information",
          title: "سطح اطلاعات",
          icon: "mdi-information",
          color: "#06b6d4",
        },
        {
          key: "constraints",
          title: "محدودیت‌ها",
          icon: "mdi-lock",
          color: "#ef4444",
        },
        {
          key: "keywords",
          title: "کلیدواژه‌ها",
          icon: "mdi-key",
          color: "#8b5cf6",
        },
        {
          key: "cta",
          title: "نوع فراخوان",
          icon: "mdi-hand-pointing-right",
          color: "#a855f7",
        },
        {
          key: "decision",
          title: "منطق تصمیم‌گیری",
          icon: "mdi-lightbulb-on",
          color: "#f97316",
        },
        {
          key: "quality",
          title: "کنترل کیفیت",
          icon: "mdi-check-circle",
          color: "#3b82f6",
        },
        {
          key: "sentiment",
          title: "تحلیل احساسی",
          icon: "mdi-emoticon-happy",
          color: "#ec4899",
        },
        {
          key: "brand",
          title: "خط مشی برند",
          icon: "mdi-brain",
          color: "#dc2626",
        },
        {
          key: "repetition",
          title: "رفتار در تکرار",
          icon: "mdi-refresh",
          color: "#84cc16",
        },
        {
          key: "additional",
          title: "اطلاعات تکمیلی",
          icon: "mdi-note-text",
          color: "#6b7280",
        },
      ],

      rules: {
        required: (v) => !!v || "این فیلد الزامی است",
        maxFive: (v) => !v || v.length <= 5 || "حداکثر ۵ مورد",
      },
    };
  },
  computed: {
    totalSections() {
      return this.sections.length;
    },
    completedSections() {
      return this.sections.filter(
        (s) => this.getSectionStatus(s.key) === "completed"
      ).length;
    },
    totalSections() {
      return 16 - this.skippedSections.length;
    },
    completedSections() {
      const sections = [
        "basic",
        "industry",
        "product",
        "goals",
        "tone",
        "structure",
        "information",
        "constraints",
        "keywords",
        "cta",
        "decision",
        "quality",
        "sentiment",
        "brand",
        "repetition",
        "additional",
      ];
      return sections.filter((section) => {
        if (this.skippedSections.includes(section)) {
          return false;
        }
        const status = this.getSectionStatus(section);
        return status === "completed" || status === "in-progress";
      }).length;
    },
    sectionColors() {
      return {
        basic: "#3b82f6",
        industry: "#10b981",
        product: "#6366f1",
        goals: "#f59e0b",
        tone: "#ec4899",
        structure: "#84cc16",
        information: "#06b6d4",
        constraints: "#ef4444",
        keywords: "#8b5cf6",
        cta: "#a855f7",
        decision: "#f97316",
        quality: "#3b82f6",
        sentiment: "#ec4899",
        brand: "#dc2626",
        repetition: "#84cc16",
        additional: "#6b7280",
      };
    },
    progressBarBackground() {
      const sections = [
        "basic",
        "industry",
        "product",
        "goals",
        "tone",
        "structure",
        "information",
        "constraints",
        "keywords",
        "cta",
        "decision",
        "quality",
        "sentiment",
        "brand",
        "repetition",
        "additional",
      ];

      const totalSections = sections.length;
      const sectionWidth = 100 / totalSections;
      const gradientStops = [];

      sections.forEach((section, index) => {
        const startPercent = (index / totalSections) * 100;
        const endPercent = ((index + 1) / totalSections) * 100;

        const color = this.sectionColors[section];
        const status = this.getSectionStatus(section);
        const isCompleted = status === "completed" || status === "in-progress";
        const displayColor = isCompleted ? color : "#e5e7eb";

        if (index === 0) {
          gradientStops.push(`${displayColor} 0%`);
        }

        gradientStops.push(`${displayColor} ${endPercent}%`);
      });

      return `linear-gradient(to right, ${gradientStops.join(", ")})`;
    },
    progressGradient() {
      return "success";
    },
    availableSubIndustries() {
      if (!this.selectedMainIndustry) return [];
      const industry = this.mainIndustries.find(
        (i) => i.id === this.selectedMainIndustry
      );
      return industry ? industry.subIndustries : [];
    },
    hasAnyChip() {
      return (
        this.formData.assistantName ||
        this.formData.assistantType ||
        this.formData.productServiceType ||
        this.formData.selectedIndustries.length > 0 ||
        this.formData.organizationType ||
        this.formData.productServiceTypeDetail ||
        this.formData.brandSpec ||
        this.formData.referenceStyle ||
        this.formData.salesGoals.length > 0 ||
        this.formData.supportGoals.length > 0 ||
        this.formData.engagementGoals.length > 0 ||
        this.formData.tone ||
        this.formData.personality ||
        this.formData.responseLength ||
        this.formData.preferredStructure ||
        this.formData.callToActionType
      );
    },
  },
  watch: {
    formData: {
      deep: true,
      handler() {
        this.updateSectionStatuses();
      },
    },
    assistantData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // اگر داده به صورت nested JSON است (با data)
          if (newVal.data) {
            this.loadAssistantData(newVal);
          } else {
            // اگر داده به صورت flat است (سازگاری با کد قبلی)
            this.loadAssistantData({ data: newVal });
          }
        } else {
          this.resetForm();
        }
      },
    },
  },
  mounted() {
    this.updateSectionStatuses();
    this.importDataFromParent();
  },
  methods: {
    loadAssistantData(jsonData) {
      if (!jsonData || !jsonData.data) {
        this.resetForm();
        return;
      }

      const data = jsonData.data;

      // Basic Info
      this.formData.assistantName = data.basic_info?.assistant_name || "";
      this.formData.assistantType = this.reverseMapValue(
        data.basic_info?.assistant_type,
        "assistantType"
      );
      this.formData.productServiceType = this.reverseMapValue(
        data.basic_info?.product_service_nature,
        "productServiceType"
      );

      // Industry
      if (data.industry) {
        const mainCategory = this.reverseMapValue(
          data.industry.main_category,
          "industryMain"
        );
        if (mainCategory) {
          const industryObj = this.mainIndustries.find(
            (i) => i.name === mainCategory
          );
          if (industryObj) {
            this.selectedMainIndustry = industryObj.id;
            this.formData.selectedMainIndustry = industryObj.name;
          }
        }

        if (
          data.industry.sub_categories &&
          Array.isArray(data.industry.sub_categories)
        ) {
          this.formData.selectedIndustries = data.industry.sub_categories;
        }
      }

      // Product Specs
      if (data.product_specs) {
        this.formData.organizationType = this.reverseMapValue(
          data.product_specs.organization_type,
          "organizationType"
        );
        this.formData.brandSpec = this.reverseMapValue(
          data.product_specs.brand_positioning,
          "brandSpec"
        );
        this.formData.referenceStyle = this.reverseMapValue(
          data.product_specs.reference_style,
          "referenceStyle"
        );
      }

      // Goals - باید به سه دسته تقسیم شوند
      if (data.goals && Array.isArray(data.goals)) {
        const salesGoalsList = [
          "introduce_product_service",
          "increase_sales",
          "recommend_suitable_product",
          "handle_objections",
          "upsell_crosssell",
          "guide_to_payment",
        ];
        const supportGoalsList = [
          "answer_faqs",
          "usage_setup_guide",
          "order_tracking",
          "returns_warranty",
          "log_ticket_complaint",
          "troubleshooting",
        ];
        const engagementGoalsList = [
          "lead_collection",
          "appointment_booking",
          "registration",
          "introduce_branches",
          "multilingual_support",
          "pre_purchase_education",
        ];

        this.formData.salesGoals = data.goals
          .filter((g) => salesGoalsList.includes(g))
          .map((g) => this.reverseMapValue(g, "goals"));
        this.formData.supportGoals = data.goals
          .filter((g) => supportGoalsList.includes(g))
          .map((g) => this.reverseMapValue(g, "goals"));
        this.formData.engagementGoals = data.goals
          .filter((g) => engagementGoalsList.includes(g))
          .map((g) => this.reverseMapValue(g, "goals"));
      }

      // Tone & Personality
      if (data.tone_personality) {
        this.formData.tone = this.reverseMapValue(
          data.tone_personality.tone,
          "tone"
        );
        this.formData.personality = this.reverseMapValue(
          data.tone_personality.personality,
          "personality"
        );
      }

      // Response Structure
      if (data.response_structure) {
        this.formData.responseLength = this.reverseMapValue(
          data.response_structure.length,
          "responseLength"
        );
        this.formData.preferredStructure = this.reverseMapValue(
          data.response_structure.format,
          "preferredStructure"
        );
      }

      // Information Level
      if (data.information_level) {
        this.formData.informationSource = this.reverseMapValue(
          data.information_level.source,
          "informationSource"
        );
        this.formData.noInformationBehavior = this.reverseMapValue(
          data.information_level.unknown_behavior,
          "noInformationBehavior"
        );
      }

      // Constraints - باید به سه دسته تقسیم شوند
      if (data.constraints && Array.isArray(data.constraints)) {
        const contentSensitivityList = [
          "no_medical_advice",
          "no_legal_advice",
          "no_financial_advice",
          "no_dangerous_technical_info",
          "no_hazardous_instructions",
        ];
        const claimControlList = [
          "no_definitive_claims",
          "no_exaggerated_claims",
          "only_verified_data",
          "no_unverified_comparison",
          "no_price_invention",
        ];
        const privacySecurityList = [
          "no_sensitive_data_request",
          "minimize_data_collection",
          "refer_sensitive_to_support",
        ];

        this.formData.contentSensitivity = data.constraints
          .filter((c) => contentSensitivityList.includes(c))
          .map((c) => this.reverseMapValue(c, "constraints"));
        this.formData.claimControl = data.constraints
          .filter((c) => claimControlList.includes(c))
          .map((c) => this.reverseMapValue(c, "constraints"));
        this.formData.privacySecurity = data.constraints
          .filter((c) => privacySecurityList.includes(c))
          .map((c) => this.reverseMapValue(c, "constraints"));
      }

      // Keywords - باید به سه دسته تقسیم شوند
      if (data.keywords && Array.isArray(data.keywords)) {
        const generalKeywordsList = [
          "price_cost",
          "discount_promotion",
          "how_to_buy",
          "shipping_delivery_time",
          "return_policy",
          "warranty",
          "support",
          "comparison",
          "availability",
          "payment_method",
        ];
        const technicalKeywordsList = [
          "features",
          "benefits_value",
          "how_to_use",
          "safety",
          "maintenance",
          "compatibility",
          "installation_setup",
          "troubleshooting",
          "energy_resource_consumption",
          "quality_standards",
        ];
        const b2bKeywordsList = [
          "bulk_purchase",
          "corporate_contract",
          "product_demo",
          "pricing_plans",
          "sla_corporate_support",
          "team_training",
        ];

        this.formData.generalKeywords = data.keywords
          .filter((k) => generalKeywordsList.includes(k))
          .map((k) => this.reverseMapValue(k, "keywords"));
        this.formData.technicalKeywords = data.keywords
          .filter((k) => technicalKeywordsList.includes(k))
          .map((k) => this.reverseMapValue(k, "keywords"));
        this.formData.b2bKeywords = data.keywords
          .filter((k) => b2bKeywordsList.includes(k))
          .map((k) => this.reverseMapValue(k, "keywords"));
      }

      // Call to Action
      if (data.call_to_action) {
        if (data.call_to_action.type === "custom_text") {
          this.formData.callToActionType = "custom";
          this.formData.customCallToAction =
            data.call_to_action.custom_text || "";
        } else {
          this.formData.callToActionType = this.reverseMapValue(
            data.call_to_action.type,
            "callToAction"
          );
        }
      }

      // Decision Logic
      if (data.decision_logic) {
        if (data.decision_logic.conflict_priority) {
          this.formData.goalPriority = [
            this.reverseMapValue(
              data.decision_logic.conflict_priority,
              "decisionLogic"
            ),
          ];
        }
        if (data.decision_logic.insufficient_data_strategy) {
          this.formData.noInfoBehavior = [
            this.reverseMapValue(
              data.decision_logic.insufficient_data_strategy,
              "decisionLogic"
            ),
          ];
        }
      }

      // Quality Control
      if (data.quality_control && Array.isArray(data.quality_control)) {
        this.formData.qualityControl = data.quality_control.map((qc) =>
          this.reverseMapValue(qc, "qualityControl")
        );
      }

      // Sentiment Analysis
      if (data.sentiment_analysis) {
        if (data.sentiment_analysis.detection_types) {
          this.formData.emotionalDetection =
            data.sentiment_analysis.detection_types.map((dt) =>
              this.reverseMapValue(dt, "sentimentDetection")
            );
        }
        if (data.sentiment_analysis.reaction) {
          this.formData.emotionalReaction = [
            this.reverseMapValue(
              data.sentiment_analysis.reaction,
              "sentimentReaction"
            ),
          ];
        }
      }

      // Brand DNA
      if (data.brand_dna) {
        if (data.brand_dna.rules) {
          this.formData.brandPolicy = data.brand_dna.rules.map((r) =>
            this.reverseMapValue(r, "brandPolicy")
          );
        }
        if (data.brand_dna.forbidden_words) {
          this.formData.bannedWords = Array.isArray(
            data.brand_dna.forbidden_words
          )
            ? data.brand_dna.forbidden_words.join(", ")
            : data.brand_dna.forbidden_words;
        }
      }

      // Repetition Behavior
      if (data.repetition_behavior) {
        if (typeof data.repetition_behavior === "string") {
          const mapped = this.reverseMapValue(
            data.repetition_behavior,
            "repetitionBehavior"
          );
          this.formData.repeatQuestionBehavior = mapped
            ? [mapped]
            : [data.repetition_behavior];
        } else if (Array.isArray(data.repetition_behavior)) {
          this.formData.repeatQuestionBehavior = data.repetition_behavior.map(
            (rb) => this.reverseMapValue(rb, "repetitionBehavior")
          );
        }
      }

      // Additional Info
      this.formData.additionalInfo = data.additional_info || "";
    },

    // Helper: تبدیل مقدار انگلیسی به فارسی (reverse mapping)
    reverseMapValue(enValue, mappingKey) {
      if (!enValue) return null;
      if (!this.mappings || !this.mappings[mappingKey]) {
        return enValue;
      }

      const mapping = this.mappings[mappingKey];
      for (const [faKey, enKey] of Object.entries(mapping)) {
        if (enKey === enValue) {
          return faKey;
        }
      }

      return enValue;
    },
    importDataFromParent() {
      this.$emit("request-data");
    },

    updateSectionStatuses() {
      this.sections.forEach((section) => {
        section.status = this.getSectionStatus(section.key);
      });
    },

    getSectionStatus(sectionKey) {
      const data = this.formData;

      switch (sectionKey) {
        case "basic":
          return data.assistantName &&
            data.assistantType &&
            data.productServiceType
            ? "completed"
            : data.assistantName ||
              data.assistantType ||
              data.productServiceType
            ? "in-progress"
            : "empty";
        case "industry":
          return data.selectedIndustries?.length > 0
            ? "completed"
            : data.selectedMainIndustry
            ? "in-progress"
            : "empty";
        case "product":
          return data.organizationType || data.brandSpec || data.referenceStyle
            ? "completed"
            : "empty";
        case "goals":
          return data.salesGoals?.length > 0 ||
            data.supportGoals?.length > 0 ||
            data.engagementGoals?.length > 0
            ? "completed"
            : "empty";
        case "tone":
          return data.tone && data.personality
            ? "completed"
            : data.tone || data.personality
            ? "in-progress"
            : "empty";
        case "structure":
          return data.responseLength && data.preferredStructure
            ? "completed"
            : data.responseLength || data.preferredStructure
            ? "in-progress"
            : "empty";
        case "information":
          return data.informationSource && data.noInformationBehavior
            ? "completed"
            : data.informationSource || data.noInformationBehavior
            ? "in-progress"
            : "empty";
        case "constraints":
          return data.contentSensitivity?.length > 0 ||
            data.claimControl?.length > 0 ||
            data.privacySecurity?.length > 0
            ? "completed"
            : "empty";
        case "keywords":
          return data.generalKeywords?.length > 0 ||
            data.technicalKeywords?.length > 0 ||
            data.b2bKeywords?.length > 0
            ? "completed"
            : "empty";
        case "cta":
          return data.callToActionType ? "completed" : "empty";
        case "decision":
          return data.goalPriority?.length > 0 ||
            data.noInfoBehavior?.length > 0
            ? "completed"
            : "empty";
        case "quality":
          return data.qualityControl?.length > 0 ? "completed" : "empty";
        case "sentiment":
          return data.emotionalDetection?.length > 0 ||
            data.emotionalReaction?.length > 0
            ? "completed"
            : "empty";
        case "brand":
          return data.brandPolicy?.length > 0 || data.bannedWords
            ? "completed"
            : "empty";
        case "repetition":
          return data.repeatQuestionBehavior?.length > 0
            ? "completed"
            : "empty";
        case "additional":
          return data.additionalInfo ? "completed" : "empty";
        default:
          return "empty";
      }
    },

    getStatusText(status) {
      const texts = {
        empty: "هنوز تکمیل نشده",
        "in-progress": "در حال تکمیل",
        completed: "تکمیل شده",
        skipped: "صرف نظر شده",
      };
      return texts[status] || texts.empty;
    },

    getStatusIcon(status) {
      const icons = {
        empty: "mdi-circle-outline",
        "in-progress": "mdi-clock-outline",
        completed: "mdi-check-circle",
        skipped: "mdi-close-circle",
      };
      return icons[status] || icons.empty;
    },

    getStatusColor(status) {
      const colors = {
        empty: "grey",
        "in-progress": "orange",
        completed: "green",
        skipped: "red",
      };
      return colors[status] || colors.empty;
    },

    getStatusTextColor(status) {
      const colors = {
        empty: "secondary",
        "in-progress": "warning",
        completed: "success",
        skipped: "error",
      };
      return colors[status] || colors.empty;
    },

    getSectionCardClass(status) {
      return {
        "section-empty": status === "empty",
        "section-in-progress": status === "in-progress",
        "section-completed": status === "completed",
        "section-skipped": status === "skipped",
      };
    },

    getStatusIconClass(status) {
      return `status-icon-${status}`;
    },

    openSection(sectionKey) {
      this.currentStep = sectionKey;
      this.bottomNav = 1;
    },

    backToList() {
      this.currentStep = "list";
      this.bottomNav = 0;
    },

    handleSave() {
      this.$emit("save");
    },

    handleCancel() {
      this.$emit("cancel");
    },

    handleReset() {
      this.$emit("reset");
    },

    // Helper methods
    clearField(field) {
      if (Array.isArray(this.formData[field])) {
        this.formData[field] = [];
      } else {
        this.formData[field] = null || "";
      }
    },

    removeFromArray(array, item) {
      const index = array.indexOf(item);
      if (index > -1) {
        array.splice(index, 1);
      }
    },

    onMainIndustryChange() {
      this.formData.selectedMainIndustry =
        this.mainIndustries.find((i) => i.id === this.selectedMainIndustry)
          ?.name || null;
      this.formData.selectedIndustries = [];
    },

    addCustomOption(fieldName) {
      this.currentCustomField = fieldName;
      this.customOptionText = "";
      this.showAddCustomDialog = true;
    },

    addCustomOptionConfirm() {
      if (this.customOptionText.trim()) {
        const fieldName = this.getFieldItemsName(this.currentCustomField);
        const field = this[fieldName];
        if (field && Array.isArray(field)) {
          field.push(this.customOptionText.trim());
          if (Array.isArray(this.formData[this.currentCustomField])) {
            this.formData[this.currentCustomField].push(
              this.customOptionText.trim()
            );
          } else {
            this.formData[this.currentCustomField] = [
              this.customOptionText.trim(),
            ];
          }
        }
        this.showAddCustomDialog = false;
        this.customOptionText = "";
        this.currentCustomField = null;
      }
    },

    getFieldItemsName(fieldName) {
      const mapping = {
        contentSensitivity: "contentSensitivities",
        claimControl: "claimControls",
        privacySecurity: "privacySecurities",
        generalKeywords: "generalKeywords",
        technicalKeywords: "technicalKeywords",
        b2bKeywords: "b2bKeywords",
        goalPriority: "goalPriorities",
        noInfoBehavior: "noInfoBehaviors",
        qualityControl: "qualityControls",
        emotionalDetection: "emotionalDetections",
        emotionalReaction: "emotionalReactions",
        brandPolicy: "brandPolicies",
        repeatQuestionBehavior: "repeatQuestionBehaviors",
      };
      return mapping[fieldName] || fieldName;
    },

    customFilter(item, queryText) {
      return item.toLowerCase().includes(queryText.toLowerCase());
    },
    // getSectionStatus(sectionKey) {
    //   if (this.skippedSections.includes(sectionKey)) {
    //     return "skipped";
    //   }

    //   const data = this.formData;

    //   switch (sectionKey) {
    //     case "basic":
    //       return data.assistantName &&
    //         data.assistantType &&
    //         data.productServiceType
    //         ? "completed"
    //         : data.assistantName ||
    //           data.assistantType ||
    //           data.productServiceType
    //         ? "in-progress"
    //         : "empty";
    //     case "industry":
    //       return data.selectedIndustries?.length > 0
    //         ? "completed"
    //         : data.selectedMainIndustry
    //         ? "in-progress"
    //         : "empty";
    //     case "product":
    //       return data.organizationType || data.brandSpec || data.referenceStyle
    //         ? "completed"
    //         : "empty";
    //     case "goals":
    //       return data.salesGoals?.length > 0 ||
    //         data.supportGoals?.length > 0 ||
    //         data.engagementGoals?.length > 0
    //         ? "completed"
    //         : "empty";
    //     case "tone":
    //       return data.tone && data.personality
    //         ? "completed"
    //         : data.tone || data.personality
    //         ? "in-progress"
    //         : "empty";
    //     case "structure":
    //       return data.responseLength && data.preferredStructure
    //         ? "completed"
    //         : data.responseLength || data.preferredStructure
    //         ? "in-progress"
    //         : "empty";
    //     case "information":
    //       return data.informationSource && data.noInformationBehavior
    //         ? "completed"
    //         : data.informationSource || data.noInformationBehavior
    //         ? "in-progress"
    //         : "empty";
    //     case "constraints":
    //       return data.contentSensitivity?.length > 0 ||
    //         data.claimControl?.length > 0 ||
    //         data.privacySecurity?.length > 0
    //         ? "completed"
    //         : "empty";
    //     case "keywords":
    //       return data.generalKeywords?.length > 0 ||
    //         data.technicalKeywords?.length > 0 ||
    //         data.b2bKeywords?.length > 0
    //         ? "completed"
    //         : "empty";
    //     case "cta":
    //       return data.callToActionType ? "completed" : "empty";
    //     case "decision":
    //       return data.goalPriority?.length > 0 ||
    //         data.noInfoBehavior?.length > 0
    //         ? "completed"
    //         : "empty";
    //     case "quality":
    //       return data.qualityControl?.length > 0 ? "completed" : "empty";
    //     case "sentiment":
    //       return data.emotionalDetection?.length > 0 ||
    //         data.emotionalReaction?.length > 0
    //         ? "completed"
    //         : "empty";
    //     case "brand":
    //       return data.brandPolicy?.length > 0 || data.bannedWords
    //         ? "completed"
    //         : "empty";
    //     case "repetition":
    //       return data.repeatQuestionBehavior?.length > 0
    //         ? "completed"
    //         : "empty";
    //     case "additional":
    //       return data.additionalInfo ? "completed" : "empty";
    //     default:
    //       return "empty";
    //   }
    // },

    getSectionStatusText(sectionKey) {
      const status = this.getSectionStatus(sectionKey);
      const texts = {
        empty: "هنوز تکمیل نشده",
        skipped: "رد شده",
        "in-progress": "در حال تکمیل",
        completed: "تکمیل شده",
      };
      return texts[status] || texts.empty;
    },

    getSectionStatusIcon(sectionKey) {
      const status = this.getSectionStatus(sectionKey);
      const icons = {
        empty: "mdi-circle-outline",
        "in-progress": "mdi-clock-outline",
        completed: "mdi-check-circle",
        skipped: "mdi-close-circle",
      };
      return icons[status] || icons.empty;
    },

    getSectionStatusColor(sectionKey) {
      const status = this.getSectionStatus(sectionKey);
      const colors = {
        empty: "grey",
        "in-progress": "orange",
        completed: "green",
        skipped: "red",
      };
      return colors[status] || colors.empty;
    },

    getSectionStatusTextColor(sectionKey) {
      const status = this.getSectionStatus(sectionKey);
      const colors = {
        empty: "secondary",
        "in-progress": "warning",
        completed: "success",
        skipped: "error",
      };
      return colors[status] || colors.empty;
    },
    skipSection(sectionKey) {
      if (!this.skippedSections.includes(sectionKey)) {
        this.skippedSections.push(sectionKey);
        this.clearSectionData(sectionKey);
      }
    },
    unskipSection(sectionKey) {
      const index = this.skippedSections.indexOf(sectionKey);
      if (index > -1) {
        this.skippedSections.splice(index, 1);
      }
    },
    clearSectionData(sectionKey) {
      const data = this.formData;
      switch (sectionKey) {
        case "industry":
          data.selectedIndustries = [];
          data.selectedMainIndustry = null;
          this.selectedMainIndustry = null;
          break;
        case "product":
          data.organizationType = null;
          data.brandSpec = null;
          data.referenceStyle = null;
          break;
        case "goals":
          data.salesGoals = [];
          data.supportGoals = [];
          data.engagementGoals = [];
          break;
        case "tone":
          data.tone = null;
          data.personality = null;
          break;
        case "structure":
          data.responseLength = null;
          data.preferredStructure = null;
          break;
        case "information":
          data.informationSource = null;
          data.noInformationBehavior = null;
          break;
        case "constraints":
          data.contentSensitivity = [];
          data.claimControl = [];
          data.privacySecurity = [];
          break;
        case "keywords":
          data.generalKeywords = [];
          data.technicalKeywords = [];
          data.b2bKeywords = [];
          break;
        case "cta":
          data.callToActionType = null;
          data.customCallToAction = "";
          break;
        case "decision":
          data.goalPriority = [];
          data.noInfoBehavior = [];
          break;
        case "quality":
          data.qualityControl = [];
          break;
        case "sentiment":
          data.emotionalDetection = [];
          data.emotionalReaction = [];
          break;
        case "brand":
          data.brandPolicy = [];
          data.bannedWords = "";
          break;
        case "repetition":
          data.repeatQuestionBehavior = [];
          break;
        case "additional":
          data.additionalInfo = "";
          break;
      }
    },

    isSectionSkipped(sectionKey) {
      return this.skippedSections.includes(sectionKey);
    },

    convertToJson() {
      const json = {
        basic_info: {
          assistant_name: this.formData.assistantName,
          assistant_type: this.mapValue(
            this.formData.assistantType,
            "assistantType"
          ),
          product_service_nature: this.mapValue(
            this.formData.productServiceType,
            "productServiceType"
          ),
        },
        industry: {
          main_category: this.mapValue(
            this.formData.selectedMainIndustry,
            "industryMain"
          ),
          sub_categories: this.formData.selectedIndustries.map((sub) =>
            this.mapValue(sub, "industrySub")
          ),
        },
        product_specs: {
          organization_type: this.mapValue(
            this.formData.organizationType,
            "organizationType"
          ),
          brand_positioning: this.mapValue(
            this.formData.brandSpec,
            "brandSpec"
          ),
          reference_style: this.mapValue(
            this.formData.referenceStyle,
            "referenceStyle"
          ),
        },
        goals: [
          ...this.formData.salesGoals.map((g) => this.mapValue(g, "goals")),
          ...this.formData.supportGoals.map((g) => this.mapValue(g, "goals")),
          ...this.formData.engagementGoals.map((g) =>
            this.mapValue(g, "goals")
          ),
        ],
        tone_personality: {
          tone: this.mapValue(this.formData.tone, "tone"),
          personality: this.mapValue(this.formData.personality, "personality"),
        },
        response_structure: {
          length: this.mapValue(this.formData.responseLength, "responseLength"),
          format: this.mapValue(
            this.formData.preferredStructure,
            "preferredStructure"
          ),
        },
        information_level: {
          source: this.mapValue(
            this.formData.informationSource,
            "informationSource"
          ),
          unknown_behavior: this.mapValue(
            this.formData.noInformationBehavior,
            "noInformationBehavior"
          ),
        },
        constraints: [
          ...this.formData.contentSensitivity.map((c) =>
            this.mapValue(c, "constraints")
          ),
          ...this.formData.claimControl.map((c) =>
            this.mapValue(c, "constraints")
          ),
          ...this.formData.privacySecurity.map((c) =>
            this.mapValue(c, "constraints")
          ),
        ],
        keywords: [
          ...this.formData.generalKeywords.map((k) =>
            this.mapValue(k, "keywords")
          ),
          ...this.formData.technicalKeywords.map((k) =>
            this.mapValue(k, "keywords")
          ),
          ...this.formData.b2bKeywords.map((k) => this.mapValue(k, "keywords")),
        ],
        call_to_action: {
          type:
            this.formData.callToActionType === "custom"
              ? "custom_text"
              : this.mapValue(this.formData.callToActionType, "callToAction"),
          custom_text:
            this.formData.callToActionType === "custom"
              ? this.formData.customCallToAction
              : null,
        },
        decision_logic: {
          conflict_priority: this.mapValue(
            this.formData.goalPriority[0],
            "decisionLogic"
          ),
          insufficient_data_strategy: this.mapValue(
            this.formData.noInfoBehavior[0],
            "decisionLogic"
          ),
        },
        quality_control: this.formData.qualityControl.map((qc) =>
          this.mapValue(qc, "qualityControl")
        ),
        sentiment_analysis: {
          detection_types: this.formData.emotionalDetection.map((dt) =>
            this.mapValue(dt, "sentimentDetection")
          ),
          reaction: this.mapValue(
            this.formData.emotionalReaction[0],
            "sentimentReaction"
          ),
        },
        brand_dna: {
          rules: this.formData.brandPolicy.map((r) =>
            this.mapValue(r, "brandPolicy")
          ),
          forbidden_words: this.formData.bannedWords
            ? this.formData.bannedWords
                .split(/[,،]/)
                .map((w) => w.trim())
                .filter((w) => w)
            : [],
        },
        repetition_behavior:
          this.formData.repeatQuestionBehavior?.length > 0
            ? this.formData.repeatQuestionBehavior[0]
            : this.mapValue(
                this.formData.repeatQuestionBehavior,
                "repetitionBehavior"
              ) || null,

        additional_info: this.formData.additionalInfo || "",
      };

      return json;
    },

    // Helper: تبدیل مقدار فارسی به انگلیسی
    mapValue(faValue, mappingKey) {
      if (!faValue) return null;
      if (!this.mappings || !this.mappings[mappingKey]) {
        return faValue;
      }

      const mapping = this.mappings[mappingKey];
      return mapping && mapping[faValue] ? mapping[faValue] : faValue;
    },
    save() {
      if (
        !this.formData.assistantName ||
        this.formData.assistantName.trim() === ""
      ) {
        this.$toast.error("لطفاً نام دستیار را وارد کنید");
        return;
      }
      if (
        !this.formData.assistantType ||
        (typeof this.formData.assistantType === "string" &&
          this.formData.assistantType.trim() === "")
      ) {
        this.$toast.error("لطفاً نوع دستیار را وارد کنید");
        return;
      }
      if (
        !this.formData.productServiceType ||
        (typeof this.formData.productServiceType === "string" &&
          this.formData.productServiceType.trim() === "")
      ) {
        this.$toast.error("لطفاً نوع خدمت یا محصول درخواستی را وارد کنید");
        return;
      }
      const jsonData = this.convertToJson();
      // JSON را emit می‌کنیم (نه formData خام)
      this.$emit("save", {
        data: jsonData,
        name: this.formData.assistantName,
        type: this.formData.assistantType,
        text: this.generatePrompt(),
      });
    },
    cancel() {
      this.resetForm();
      this.$emit("cancel");
    },
    refreshForm() {
      if (this.assistantData) {
        if (this.assistantData.data) {
          this.loadAssistantData(this.assistantData);
        } else {
          this.loadAssistantData({ data: this.assistantData });
        }
      } else {
        this.resetForm();
      }

      this.$nextTick(() => {
        this.updateSectionStatuses();
      });
    },
    removeBannedWord(word) {
      const words = this.formData.bannedWords
        .split(/[,،]/)
        .map((w) => w.trim())
        .filter((w) => w);
      const index = words.indexOf(word);
      if (index > -1) {
        words.splice(index, 1);
        this.formData.bannedWords = words.join(", ");
      }
    },

    clearField(field) {
      if (Array.isArray(this.formData[field])) {
        this.formData[field] = [];
      } else {
        this.formData[field] = null || "";
      }
    },

    clearCallToAction() {
      this.formData.callToActionType = null;
      this.formData.customCallToAction = "";
    },

    onMainIndustryChange() {
      this.formData.selectedMainIndustry =
        this.mainIndustries.find((i) => i.id === this.selectedMainIndustry)
          ?.name || null;
      this.formData.selectedIndustries = [];
    },
    onAssistantTypeChange() {
      // منطق تغییرات در صورت نیاز
    },

    removeIndustry(item) {
      const index = this.formData.selectedIndustries.indexOf(item);
      if (index > -1) {
        this.formData.selectedIndustries.splice(index, 1);
      }
    },

    removeFromArray(array, item) {
      const index = array.indexOf(item);
      if (index > -1) {
        this.$delete(array, index);

        // array.splice(index, 1);
      }
    },

    addCustomOption(fieldName) {
      this.currentCustomField = fieldName;
      this.customOptionText = "";
      this.showAddCustomDialog = true;
    },

    addCustomOptionConfirm() {
      if (this.customOptionText.trim()) {
        const field = this[this.getFieldItemsName(this.currentCustomField)];
        if (field && Array.isArray(field)) {
          field.push(this.customOptionText.trim());
          if (Array.isArray(this.formData[this.currentCustomField])) {
            this.formData[this.currentCustomField].push(
              this.customOptionText.trim()
            );
          } else {
            this.formData[this.currentCustomField] = [
              this.customOptionText.trim(),
            ];
          }
        }
        this.showAddCustomDialog = false;
        this.customOptionText = "";
        this.currentCustomField = null;
      }
    },

    getFieldItemsName(fieldName) {
      const mapping = {
        contentSensitivity: "contentSensitivities",
        claimControl: "claimControls",
        privacySecurity: "privacySecurities",
        generalKeywords: "generalKeywords",
        technicalKeywords: "technicalKeywords",
        b2bKeywords: "b2bKeywords",
        goalPriority: "goalPriorities",
        noInfoBehavior: "noInfoBehaviors",
        qualityControl: "qualityControls",
        emotionalDetection: "emotionalDetections",
        emotionalReaction: "emotionalReactions",
        brandPolicy: "brandPolicies",
        repeatQuestionBehavior: "repeatQuestionBehaviors",
      };
      return mapping[fieldName] || fieldName;
    },

    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
      this.skippedSections = [];

      this.formData = {
        assistantName: "",
        assistantType: null,
        productServiceType: null,
        selectedIndustries: [],
        organizationType: null,
        productServiceTypeDetail: null,
        brandSpec: null,
        referenceStyle: null,
        salesGoals: [],
        supportGoals: [],
        engagementGoals: [],
        tone: null,
        personality: null,
        responseLength: null,
        preferredStructure: null,
        informationSource: null,
        noInformationBehavior: null,
        contentSensitivity: [],
        claimControl: [],
        privacySecurity: [],
        generalKeywords: [],
        technicalKeywords: [],
        b2bKeywords: [],
        callToActionType: null,
        customCallToAction: "",
        goalPriority: [],
        noInfoBehavior: [],
        qualityControl: [],
        emotionalDetection: [],
        emotionalReaction: [],
        brandPolicy: [],
        bannedWords: "",
        repeatQuestionBehavior: [],
        additionalInfo: "",
      };
      this.selectedMainIndustry = null;
      this.panels = 0;
      this.contentSensitivities = [
        "عدم ارائه توصیه پزشکی",
        "عدم ارائه مشاوره حقوقی",
        "عدم ارائه مشاوره مالی/سرمایه‌گذاری",
        "عدم ارائه اطلاعات فنی خطرناک",
        "عدم ارائه دستور ساخت مواد/ابزار خطرناک",
      ];

      this.claimControls = [
        "عدم ادعاهای قطعی/درمانی",
        "عدم اغراق تبلیغاتی",
        "فقط با داده معتبر (برچسب/کاتالوگ)",
        "عدم مقایسه بدون داده معتبر",
        "عدم قیمت‌سازی (اگر قیمت نداریم)",
      ];

      this.privacySecurities = [
        "عدم درخواست اطلاعات حساس (کارت/رمز)",
        "حداقل‌سازی جمع‌آوری داده",
        "ارجاع به پشتیبانی برای موارد حساس",
      ];

      this.generalKeywords = [
        "قیمت / هزینه",
        "تخفیف / جشنواره",
        "نحوه خرید",
        "ارسال / زمان تحویل",
        "شرایط مرجوعی",
        "گارانتی",
        "پشتیبانی",
        "مقایسه",
        "موجودی",
        "روش پرداخت",
      ];

      this.technicalKeywords = [
        "ویژگی‌ها",
        "مزایا و ارزش‌ها",
        "نحوه استفاده",
        "ایمنی",
        "نگهداری",
        "سازگاری",
        "نصب / راه‌اندازی",
        "عیب‌یابی",
        "مصرف انرژی/منابع",
        "کیفیت/استانداردها",
      ];

      this.b2bKeywords = [
        "خرید عمده",
        "قرارداد سازمانی",
        "دموی محصول",
        "قیمت‌گذاری پلن‌ها",
        "SLA و پشتیبانی سازمانی",
        "آموزش تیم",
      ];

      this.goalPriorities = [
        "فروش بر پشتیبانی مقدم است",
        "پشتیبانی بر فروش مقدم است",
        "راهنمایی بی‌طرفانه (بدون فشار)",
        "بسته به نوع سؤال تصمیم‌گیری شود",
      ];

      this.noInfoBehaviors = [
        "توقف پاسخ و اعلام عدم‌اطلاعی",
        "درخواست اطلاعات تکمیلی",
        "ارائه راهنمای کلی ایمن",
        "پیشنهاد مسیر جایگزین",
      ];

      this.qualityControls = [
        "بررسی تطابق پاسخ با محدودیت‌ها",
        "جلوگیری از اغراق تبلیغاتی",
        "حذف حدس و فرض‌های نامطمئن",
        "ساده‌سازی زبان در پاسخ‌های پیچیده",
        "کوتاه‌سازی پاسخ‌های بیش‌ازحد طولانی",
      ];

      this.emotionalDetections = [
        "تشخیص نارضایتی یا عصبانیت",
        "تشخیص تردید در خرید",
        "تشخیص عجله یا کم‌حوصلگی",
        "تشخیص نیاز به اطمینان‌بخشی",
      ];

      this.emotionalReactions = [
        "کاهش لحن فروش",
        "همدلی و عذرخواهی حرفه‌ای",
        "ارائه پاسخ کوتاه و سریع",
        "ارجاع مستقیم به پشتیبانی انسانی",
      ];

      this.brandPolicies = [
        "عدم استفاده از ایموجی",
        "عدم شوخی یا لحن خودمانی",
        "عدم اشاره به رقبا",
        "عدم مقایسه مستقیم",
        "استفاده از واژگان رسمی برند",
      ];

      this.repeatQuestionBehaviors = [
        "پاسخ خلاصه‌تر از قبل",
        "ارجاع به پاسخ قبلی",
        "ارائه نسخه مرحله‌ای",
        "پرسش برای شفاف‌سازی نیاز کاربر",
      ];
    },
    jsonToPrompt(jsonData) {
      if (!jsonData) return "";

      // متن پایه
      let prompt = `<< این دستیار نمایندهٔ رسمی اطلاعات و راهنمایی‌های مرتبط با محصول/خدمت تعریف‌شده در این پرامپت است.
تمام پاسخ‌ها باید مستقیماً به این محصول یا خدمات وابسته به آن مربوط باشند.
در صورت دریافت سؤال نامرتبط، از پاسخ‌گویی خودداری کرده و با حفظ لحن حرفه‌ای، کاربر را به طرح سؤال مرتبط با محصول هدایت کنید. >>

# مشخصات دستیار

## مشخصات پایه
- نام دستیار: ${jsonData.basic_info?.assistant_name || "تعیین نشده"}
- نوع دستیار: ${jsonData.basic_info?.assistant_type || "تعیین نشده"}
- نوع محصول/خدمت: ${jsonData.basic_info?.product_service_nature || "تعیین نشده"}
`;

      // افزودن اطلاعات صنعت
      if (jsonData.industry?.sub_categories?.length > 0) {
        prompt += `\n## صنعت/حوزه فعالیت`;
        if (jsonData.industry.main_category) {
          prompt += `\n- صنعت اصلی: ${jsonData.industry.main_category}`;
        }
        prompt += `\n${jsonData.industry.sub_categories
          .map((ind) => `- ${ind}`)
          .join("\n")}`;
      }

      // افزودن مشخصات محصول
      if (jsonData.product_specs) {
        const specs = jsonData.product_specs;
        if (
          specs.organization_type ||
          specs.brand_positioning ||
          specs.reference_style
        ) {
          prompt += `\n## مشخصات محصول/خدمت`;
          if (specs.organization_type) {
            prompt += `\n- نوع سازمان: ${specs.organization_type}`;
          }
          if (specs.brand_positioning) {
            prompt += `\n- مشخصات برند: ${specs.brand_positioning}`;
          }
          if (specs.reference_style) {
            prompt += `\n- نحوه اشاره در پاسخ: ${specs.reference_style}`;
          }
        }
      }

      // افزودن اهداف
      if (jsonData.goals?.length > 0) {
        prompt += `\n## هدف دستیار\n${jsonData.goals
          .map((g) => `- ${g}`)
          .join("\n")}`;
      }

      // لحن و شخصیت
      if (jsonData.tone_personality) {
        const tp = jsonData.tone_personality;
        if (tp.tone || tp.personality) {
          prompt += `\n## لحن و شخصیت`;
          if (tp.tone) {
            prompt += `\n- لحن: ${tp.tone}`;
          }
          if (tp.personality) {
            prompt += `\n- شخصیت: ${tp.personality}`;
          }
        }
      }

      // ساختار پاسخ
      if (jsonData.response_structure) {
        const rs = jsonData.response_structure;
        if (rs.length || rs.format) {
          prompt += `\n## ساختار پاسخ`;
          if (rs.length) {
            prompt += `\n- طول پاسخ: ${rs.length}`;
          }
          if (rs.format) {
            prompt += `\n- ساختار: ${rs.format}`;
          }
        }
      }

      // سطح اطلاعات
      if (jsonData.information_level) {
        const il = jsonData.information_level;
        if (il.source || il.unknown_behavior) {
          prompt += `\n## سطح مجاز اطلاعات`;
          if (il.source) {
            prompt += `\n- منبع: ${il.source}`;
          }
          if (il.unknown_behavior) {
            prompt += `\n- رفتار در نبود اطلاعات: ${il.unknown_behavior}`;
          }
        }
      }

      // محدودیت‌ها
      if (jsonData.constraints?.length > 0) {
        prompt += `\n## محدودیت‌ها\n${jsonData.constraints
          .map((c) => `- ${c}`)
          .join("\n")}`;
      }

      // کلیدواژه‌ها
      if (jsonData.keywords?.length > 0) {
        prompt += `\n## کلیدواژه‌های مهم\n${jsonData.keywords
          .map((k) => `- ${k}`)
          .join("\n")}`;
      }

      // فراخوان
      if (jsonData.call_to_action) {
        const cta = jsonData.call_to_action;
        if (cta.type) {
          prompt += `\n## نوع فراخوان: `;
          if (cta.type === "custom_text" && cta.custom_text) {
            prompt += cta.custom_text;
          } else {
            prompt += cta.type;
          }
        }
      }

      // منطق تصمیم‌گیری
      if (jsonData.decision_logic) {
        const dl = jsonData.decision_logic;
        if (dl.conflict_priority || dl.insufficient_data_strategy) {
          prompt += `\n## منطق تصمیم‌گیری`;
          if (dl.conflict_priority) {
            prompt += `\n- اولویت اهداف: ${dl.conflict_priority}`;
          }
          if (dl.insufficient_data_strategy) {
            prompt += `\n- رفتار در نبود اطلاعات: ${dl.insufficient_data_strategy}`;
          }
        }
      }

      // کنترل کیفیت
      if (jsonData.quality_control?.length > 0) {
        prompt += `\n## کنترل کیفیت\n${jsonData.quality_control
          .map((q) => `- ${q}`)
          .join("\n")}`;
      }

      // تشخیص احساسی
      if (jsonData.sentiment_analysis) {
        const sa = jsonData.sentiment_analysis;
        if (sa.detection_types?.length > 0 || sa.reaction) {
          prompt += `\n## تشخیص وضعیت احساسی`;
          if (sa.detection_types?.length > 0) {
            prompt += `\n- نوع تشخیص:\n${sa.detection_types
              .map((d) => `  - ${d}`)
              .join("\n")}`;
          }
          if (sa.reaction) {
            prompt += `\n- واکنش: ${sa.reaction}`;
          }
        }
      }

      // خط مشی برند
      if (jsonData.brand_dna) {
        const bd = jsonData.brand_dna;
        if (bd.rules?.length > 0 || bd.forbidden_words?.length > 0) {
          prompt += `\n## خط مشی و DNA برند`;
          if (bd.rules?.length > 0) {
            prompt += `\n${bd.rules.map((r) => `- ${r}`).join("\n")}`;
          }
          if (bd.forbidden_words?.length > 0) {
            prompt += `\n- کلمات ممنوعه: ${bd.forbidden_words.join(", ")}`;
          }
        }
      }

      // رفتار در تکرار سوال
      if (jsonData.repetition_behavior) {
        prompt += `\n## رفتار در تکرار سوال\n- ${jsonData.repetition_behavior}`;
      }

      // اطلاعات تکمیلی
      if (jsonData.additional_info) {
        prompt += `\n## اطلاعات تکمیلی\n${jsonData.additional_info}`;
      }

      return prompt;
    },
    generatePrompt() {
      //  this.loading = true;

      // متن پایه
      let prompt = `
# مشخصات دستیار

## مشخصات پایه
- نام دستیار: ${this.formData.assistantName}
- نوع دستیار: ${this.formData.assistantType || "تعیین نشده"}
- نوع محصول/خدمت: ${this.formData.productServiceType || "تعیین نشده"}
`;

      // افزودن اطلاعات صنعت
      if (this.formData.selectedIndustries.length > 0) {
        prompt += `\n## صنعت/حوزه فعالیت
${this.formData.selectedIndustries.map((ind) => `- ${ind}`).join("\n")}`;
      }

      // افزودن مشخصات محصول
      if (
        this.formData.organizationType ||
        this.formData.productServiceTypeDetail ||
        this.formData.brandSpec ||
        this.formData.referenceStyle
      ) {
        prompt += `\n## مشخصات محصول/خدمت`;
        if (this.formData.organizationType) {
          prompt += `\n- نوع سازمان: ${this.formData.organizationType}`;
        }
        if (this.formData.productServiceTypeDetail) {
          prompt += `\n- نوع محصول/خدمت: ${this.formData.productServiceTypeDetail}`;
        }
        if (this.formData.brandSpec) {
          prompt += `\n- مشخصات برند: ${this.formData.brandSpec}`;
        }
        if (this.formData.referenceStyle) {
          prompt += `\n- نحوه اشاره در پاسخ: ${this.formData.referenceStyle}`;
        }
      }

      // افزودن اهداف
      const allGoals = [
        ...this.formData.salesGoals.map((g) => `فروش: ${g}`),
        ...this.formData.supportGoals.map((g) => `پشتیبانی: ${g}`),
        ...this.formData.engagementGoals.map((g) => `جذب: ${g}`),
      ];
      if (allGoals.length > 0) {
        prompt += `\n## هدف دستیار\n${allGoals
          .map((g) => `- ${g}`)
          .join("\n")}`;
      }

      // لحن و شخصیت
      if (this.formData.tone || this.formData.personality) {
        prompt += `\n## لحن و شخصیت`;
        if (this.formData.tone) {
          prompt += `\n- لحن: ${this.formData.tone}`;
        }
        if (this.formData.personality) {
          prompt += `\n- شخصیت: ${this.formData.personality}`;
        }
      }

      // ساختار پاسخ
      if (this.formData.responseLength || this.formData.preferredStructure) {
        prompt += `\n## ساختار پاسخ`;
        if (this.formData.responseLength) {
          prompt += `\n- طول پاسخ: ${this.formData.responseLength}`;
        }
        if (this.formData.preferredStructure) {
          prompt += `\n- ساختار: ${this.formData.preferredStructure}`;
        }
      }

      // سطح اطلاعات
      if (
        this.formData.informationSource ||
        this.formData.noInformationBehavior
      ) {
        prompt += `\n## سطح مجاز اطلاعات`;
        if (this.formData.informationSource) {
          prompt += `\n- منبع: ${this.formData.informationSource}`;
        }
        if (this.formData.noInformationBehavior) {
          prompt += `\n- رفتار در نبود اطلاعات: ${this.formData.noInformationBehavior}`;
        }
      }

      // محدودیت‌ها
      const allLimitations = [
        ...this.formData.contentSensitivity,
        ...this.formData.claimControl,
        ...this.formData.privacySecurity,
      ];
      if (allLimitations.length > 0) {
        prompt += `\n## محدودیت‌ها\n${allLimitations
          .map((l) => `- ${l}`)
          .join("\n")}`;
      }

      // کلیدواژه‌ها
      const allKeywords = [
        ...this.formData.generalKeywords,
        ...this.formData.technicalKeywords,
        ...this.formData.b2bKeywords,
      ];
      if (allKeywords.length > 0) {
        prompt += `\n## کلیدواژه‌های مهم\n${allKeywords
          .map((k) => `- ${k}`)
          .join("\n")}`;
      }

      // فراخوان
      if (this.formData.callToActionType) {
        prompt += `\n## نوع فراخوان: `;
        if (
          this.formData.callToActionType === "custom" &&
          this.formData.customCallToAction
        ) {
          prompt += this.formData.customCallToAction;
        } else {
          prompt += this.formData.callToActionType;
        }
      }

      // منطق تصمیم‌گیری
      if (
        this.formData.goalPriority.length > 0 ||
        this.formData.noInfoBehavior.length > 0
      ) {
        prompt += `\n## منطق تصمیم‌گیری`;
        if (this.formData.goalPriority.length > 0) {
          prompt += `\n- اولویت اهداف:\n${this.formData.goalPriority
            .map((p) => `  - ${p}`)
            .join("\n")}`;
        }
        if (this.formData.noInfoBehavior.length > 0) {
          prompt += `\n- رفتار در نبود اطلاعات:\n${this.formData.noInfoBehavior
            .map((b) => `  - ${b}`)
            .join("\n")}`;
        }
      }

      // کنترل کیفیت
      if (this.formData.qualityControl.length > 0) {
        prompt += `\n## کنترل کیفیت\n${this.formData.qualityControl
          .map((q) => `- ${q}`)
          .join("\n")}`;
      }

      // تشخیص احساسی
      if (
        this.formData.emotionalDetection.length > 0 ||
        this.formData.emotionalReaction.length > 0
      ) {
        prompt += `\n## تشخیص وضعیت احساسی`;
        if (this.formData.emotionalDetection.length > 0) {
          prompt += `\n- نوع تشخیص:\n${this.formData.emotionalDetection
            .map((d) => `  - ${d}`)
            .join("\n")}`;
        }
        if (this.formData.emotionalReaction.length > 0) {
          prompt += `\n- واکنش:\n${this.formData.emotionalReaction
            .map((r) => `  - ${r}`)
            .join("\n")}`;
        }
      }

      // خط مشی برند
      if (this.formData.brandPolicy.length > 0 || this.formData.bannedWords) {
        prompt += `\n## خط مشی و DNA برند`;
        if (this.formData.brandPolicy.length > 0) {
          prompt += `\n${this.formData.brandPolicy
            .map((p) => `- ${p}`)
            .join("\n")}`;
        }
        if (this.formData.bannedWords) {
          prompt += `\n- کلمات ممنوعه: ${this.formData.bannedWords}`;
        }
      }

      // رفتار در تکرار سوال
      if (this.formData.repeatQuestionBehavior.length > 0) {
        prompt += `\n## رفتار در تکرار سوال\n${this.formData.repeatQuestionBehavior
          .map((b) => `- ${b}`)
          .join("\n")}`;
      }

      // اطلاعات تکمیلی
      if (this.formData.additionalInfo) {
        prompt += `\n## اطلاعات تکمیلی\n${this.formData.additionalInfo}`;
      }

      this.generatedPrompt = prompt;
      return prompt;
      //  this.showPromptDialog = true;
      //  this.loading = false;
    },

    copyPrompt() {
      navigator.clipboard.writeText(this.generatedPrompt).then(() => {
        this.$toast.success("پرامپت کپی شد!");
      });
    },
  },
};
</script>

<style scoped>
.mobile-assistant-builder {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: 80px;
}

.section-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid transparent;
}

.section-card:hover {
  transform: translateX(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.section-empty {
  border-left-color: #9ca3af;
}

.section-in-progress {
  border-left-color: #f59e0b;
}

.section-completed {
  border-left-color: #10b981;
}

.section-skipped {
  border-left-color: #ef4444;
}

.section-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.status-icon-empty {
  background: #f3f4f6;
}

.status-icon-in-progress {
  background: #fef3c7;
}

.status-icon-completed {
  background: #d1fae5;
}

.status-icon-skipped {
  background: #fee2e2;
}

.section-content-card {
  margin-bottom: 16px;
}

.action-buttons-card {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
}

/* Chip Styles - same as desktop but optimized for mobile */
.chip-base,
.chip-industry,
.chip-product {
  height: 32px !important;
  font-size: 0.85rem !important;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .mobile-assistant-builder {
    padding-bottom: 70px;
  }
}
.dcolumn {
  display: flex;
  flex-direction: column;
}
</style>
